import { useIntl, getLocale } from 'umi';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { appendFields2ListData, appendDynamicFields2ListData } from '@intelligence/utils/commonUtils';
import { generateDataSourceMetricKey } from '@intelligence/utils/topchartsUtils';
import InfoDoc from '@intelligence/_locales/en-US';
import InfoDocCN from '@intelligence/_locales/zh-CN';
import { gsdSegmentList } from './gsdMetricsOptions';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import type { IDateTypely } from '@intelligence/types/common';
import { segType, dataSourceType, SUM, AVG, MAX } from '@intelligence/pages/TopCharts/_config/enums';
import { GSD_SOURCE_KEY, GSD_METRIC_KEY_MAP } from '@intelligence/_config/sourceMetric';
import { SENSORTOWER_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import { Icon } from 'tea-component';
import { resolveAllMetricsMetricBoxMeta } from '@intelligence/pages/TopCharts/_config/metricBoxGroupUtils';
import {
  PC_CONSOLE_DISPLAY_CATEGORY_ORDER,
  getPCConsoleDisplayCategory,
  getPCConsoleMetricDisplayCategory,
  PC_CONSOLE_DISPLAY_CATEGORY_FIELDS,
  getPCConsoleSourceOrder,
} from './pcConsoleMetricOrdering';

export const PC_CONSOLE_DEFAULT_METRICS = [
  generateDataSourceMetricKey('revenue', 'digitalRevenue'),
  generateDataSourceMetricKey('revenue', 'units'),
  generateDataSourceMetricKey('revenue', GSD_METRIC_KEY_MAP.DIGITAL_UNITS),
  generateDataSourceMetricKey('revenue', GSD_METRIC_KEY_MAP.PHYSICAL_UNITS),

  // Steam Data 分组（Gamalytic 指标在内部排在最前）
  // Default metrics:
  // - PCU / ACU 优先使用 source_steam（仅 source_steam 提供这两个指标）；
  // - Followers / Reviews / Est Revenue / Est Units 等 source_steam 未提供的指标，
  //   默认改用 alinea 替代原先的 gamalytic。
  generateDataSourceMetricKey('steamData', 'source_steam__pcu'),
  generateDataSourceMetricKey('steamData', 'source_steam__acu'),
  generateDataSourceMetricKey('alinea', 'alinea__steamfollowers'),
  generateDataSourceMetricKey('alinea', 'alinea__steamreviews'),
  generateDataSourceMetricKey('alinea', 'alinea__est_revenue'),
  generateDataSourceMetricKey('alinea', 'alinea__est_units'),

  generateDataSourceMetricKey('mau', 'ampere_mau'),
  generateDataSourceMetricKey('engagement', 'ampere_lifetime_users'),
  generateDataSourceMetricKey('engagement', 'ampere_avg_monthly_playtime'),
  generateDataSourceMetricKey('engagement', 'ampere_acquisition_rate'),
  generateDataSourceMetricKey('engagement', 'ampere_stickiness'),
];

export const PC_CONSOLE_DATABRAIN_DEFAULT_METRICS = [
  generateDataSourceMetricKey('revenue', 'databrain__total_revenue'),
  generateDataSourceMetricKey('revenue', 'databrain__lifetime_total_revenue'),
  generateDataSourceMetricKey('revenue', 'databrain__base_game_sales_units'),
  generateDataSourceMetricKey('revenue', 'databrain__lifetime_base_game_sales_units'),
];

const PC_CONSOLE_DAILY_GRANULARITY_METRICS = [
  generateDataSourceMetricKey('dau', 'ampere_dau'),
  generateDataSourceMetricKey('dau', 'ampere_new_dau'),
  generateDataSourceMetricKey('alinea', 'alinea__dau'),
  generateDataSourceMetricKey('consoleData', 'alinea__ps_dau'),
  generateDataSourceMetricKey('consoleData', 'alinea__xbox_dau'),
  generateDataSourceMetricKey('engagement', 'ampere_avg_dailly_playtime'),

  generateDataSourceMetricKey('retention', 'ampere_d1'),
  generateDataSourceMetricKey('retention', 'ampere_d7'),
  generateDataSourceMetricKey('retention', 'ampere_d14'),
  generateDataSourceMetricKey('retention', 'ampere_d28'),
  generateDataSourceMetricKey('retention', 'ampere_d60'),
  generateDataSourceMetricKey('retention', 'ampere_rolling_d1'),
  generateDataSourceMetricKey('retention', 'ampere_rolling_d7'),
  generateDataSourceMetricKey('retention', 'ampere_rolling_d14'),
  generateDataSourceMetricKey('retention', 'ampere_rolling_d28'),
  generateDataSourceMetricKey('retention', 'ampere_rolling_d60'),
];
const PC_CONSOLE_WEEKLY_GRANULARITY_METRICS = [
  // generateDataSourceMetricKey('japanSales', 'famitsu_sales'),
  // generateDataSourceMetricKey('japanSales', 'famitsu_price'),
  // generateDataSourceMetricKey('japanSales', 'famitsu_revenue'),
  // generateDataSourceMetricKey('japanSales', 'famitsu_cumulative_sales'),
  // generateDataSourceMetricKey('japanSales', 'famitsu_cumulative_revenue'),
  generateDataSourceMetricKey('revenue', GSD_METRIC_KEY_MAP.DIGITAL_UNITS),
  generateDataSourceMetricKey('revenue', GSD_METRIC_KEY_MAP.PHYSICAL_UNITS),
  generateDataSourceMetricKey('revenue', GSD_METRIC_KEY_MAP.DIGITAL_REVENUE),
  generateDataSourceMetricKey('revenue', GSD_METRIC_KEY_MAP.PHYSICAL_REVENUE),
];

const PC_CONSOLE_MONTHLY_GRANULARITY_METRICS = [
  generateDataSourceMetricKey('revenue', 'revenue'),
  generateDataSourceMetricKey('revenue', 'physical_units'),
  generateDataSourceMetricKey('noUpdate', 'mau'),
  generateDataSourceMetricKey('noUpdate', 'player_share'),
  generateDataSourceMetricKey('noUpdate', 'mau_growth'),
  generateDataSourceMetricKey('noUpdate', 'mau_new'),
  generateDataSourceMetricKey('noUpdate', 'mau_returning'),
  generateDataSourceMetricKey('noUpdate', 'lifetime_player'),
  generateDataSourceMetricKey('noUpdate', 'avg_daily_playtime'),
  generateDataSourceMetricKey('noUpdate', 'avg_daily_sessions'),
  generateDataSourceMetricKey('noUpdate', 'avg_days_played'),
  generateDataSourceMetricKey('noUpdate', 'avg_monthly_playtime'),
  generateDataSourceMetricKey('noUpdate', 'avg_monthly_sessions'),
  generateDataSourceMetricKey('noUpdate', 'avg_playtime_per_session'),
  generateDataSourceMetricKey('noUpdate', 'stickiness'),
  generateDataSourceMetricKey('noUpdate', 'total_hours'),
  generateDataSourceMetricKey('noUpdate', 'churn'),
  generateDataSourceMetricKey('noUpdate', 'acquisition'),
  generateDataSourceMetricKey('noUpdate', 'time_spent_0H'),
  generateDataSourceMetricKey('noUpdate', 'time_spent_1H'),
  generateDataSourceMetricKey('noUpdate', 'time_spent_5H'),
  generateDataSourceMetricKey('noUpdate', 'time_spent_10H'),
  generateDataSourceMetricKey('noUpdate', 'time_spent_25H'),
  generateDataSourceMetricKey('noUpdate', 'time_spent_50H'),
  generateDataSourceMetricKey('noUpdate', 'retention_d1'),
  generateDataSourceMetricKey('noUpdate', 'retention_d2'),
  generateDataSourceMetricKey('noUpdate', 'retention_d4'),
  generateDataSourceMetricKey('noUpdate', 'retention_d7'),
  generateDataSourceMetricKey('noUpdate', 'retention_d14'),
  generateDataSourceMetricKey('noUpdate', 'retention_d28'),

  generateDataSourceMetricKey('mau', 'ampere_mau'),
  generateDataSourceMetricKey('mau', 'ampere_previous_mau'),
  generateDataSourceMetricKey('mau', 'ampere_added_mau'),
  generateDataSourceMetricKey('mau', 'ampere_new_mau'),
  generateDataSourceMetricKey('mau', 'ampere_retained_mau'),
  generateDataSourceMetricKey('mau', 'ampere_churned_mau'),
  generateDataSourceMetricKey('alinea', 'alinea__mau'),
  generateDataSourceMetricKey('consoleData', 'alinea__ps_mau'),
  generateDataSourceMetricKey('consoleData', 'alinea__xbox_mau'),
  generateDataSourceMetricKey('engagement', 'ampere_avg_monthly_playtime'),
  generateDataSourceMetricKey('engagement', 'ampere_acquisition_rate'),
  generateDataSourceMetricKey('engagement', 'ampere_stickiness'),
  generateDataSourceMetricKey('engagement', 'ampere_lifetime_users'),
];

const PC_CONSOLE_ALL_GRANULARITIES_METRICS = [
  generateDataSourceMetricKey('revenue', 'digitalRevenue'),
  generateDataSourceMetricKey('revenue', 'units'),
  generateDataSourceMetricKey('revenue', 'digitalARPPU'),
  generateDataSourceMetricKey('revenue', 'ast'),
  generateDataSourceMetricKey('revenue', 'mscienceLifetimeDigitalRevenue'),
  generateDataSourceMetricKey('revenue', 'mscienceLifetimeDigitalUnits'),
  ...PC_CONSOLE_DATABRAIN_DEFAULT_METRICS,

  generateDataSourceMetricKey('steamData', 'source_steam__pcu'),
  generateDataSourceMetricKey('steamData', 'source_steam__acu'),
  generateDataSourceMetricKey('steamData', 'pcu'),
  generateDataSourceMetricKey('steamData', 'acu'),
  generateDataSourceMetricKey('steamData', 'steamfollowers'),
  generateDataSourceMetricKey('steamData', 'steamreviews'),
  generateDataSourceMetricKey('steamData', 'est_price'),
  generateDataSourceMetricKey('steamData', 'est_revenue'),
  generateDataSourceMetricKey('steamData', 'est_cumulative_revenue'),
  generateDataSourceMetricKey('steamData', 'est_units'),
  generateDataSourceMetricKey('steamData', 'est_cumulative_units'),
  generateDataSourceMetricKey('steamData', 'weighted_price'),
  generateDataSourceMetricKey('steamData', 'wishlists'),
  generateDataSourceMetricKey('steamData', 'wishlists_total'),

  // Steam 平均游玩时间（分钟）在日/周/月均有数据，需纳入全粒度白名单；Gamalytic 版本单位为小时，不在此放开。
  generateDataSourceMetricKey('alinea', 'alinea__playtime'),

  // Alinea (same amount of metrics as Steam Data, but values are different)
  generateDataSourceMetricKey('alinea', 'alinea__pcu'),
  generateDataSourceMetricKey('alinea', 'alinea__acu'),
  generateDataSourceMetricKey('alinea', 'alinea__steamfollowers'),
  generateDataSourceMetricKey('alinea', 'alinea__steamreviews'),
  generateDataSourceMetricKey('alinea', 'alinea__players'),
  generateDataSourceMetricKey('alinea', 'alinea__players_total'),
  generateDataSourceMetricKey('alinea', 'alinea__cumulative_reviews'),
  generateDataSourceMetricKey('alinea', 'alinea__positive'),
  generateDataSourceMetricKey('alinea', 'alinea__cumulative_positive'),
  generateDataSourceMetricKey('alinea', 'alinea__est_price'),
  generateDataSourceMetricKey('alinea', 'alinea__est_revenue'),
  generateDataSourceMetricKey('alinea', 'alinea__est_cumulative_revenue'),
  generateDataSourceMetricKey('alinea', 'alinea__est_units'),
  generateDataSourceMetricKey('alinea', 'alinea__est_cumulative_units'),
  generateDataSourceMetricKey('alinea', 'alinea__wishlists'),
  generateDataSourceMetricKey('alinea', 'alinea__wishlists_total'),

  // Gamalytic (same amount of metrics as Steam Data, but values are different)
  generateDataSourceMetricKey('gamalytic', 'gamalytic__pcu'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__acu'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__steamfollowers'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__steamreviews'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__cumulative_reviews'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__est_price'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__weighted_price'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__est_revenue'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__est_cumulative_revenue'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__est_units'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__est_cumulative_units'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__wishlists'),
  generateDataSourceMetricKey('gamalytic', 'gamalytic__wishlists_total'),

  // Console Data (Alinea PS / Xbox)
  ...[
    'alinea__ps_est_revenue',
    'alinea__ps_est_cumulative_revenue',
    'alinea__ps_est_units',
    'alinea__ps_est_cumulative_units',
    'alinea__ps_players',
    'alinea__ps_players_total',
    'alinea__ps_game_pass_players',
    'alinea__ps_game_pass_players_total',
    'alinea__ps_price',
    'alinea__xbox_est_revenue',
    'alinea__xbox_est_cumulative_revenue',
    'alinea__xbox_est_units',
    'alinea__xbox_est_cumulative_units',
    'alinea__xbox_players',
    'alinea__xbox_players_total',
    'alinea__xbox_game_pass_players',
    'alinea__xbox_game_pass_players_total',
    'alinea__xbox_price',
  ].map((metric) => generateDataSourceMetricKey('consoleData', metric)),
];

export const PC_CONSOLE_GRANULARITIES_METRICS_MAPPING: {
  [key in IDateTypely]: string[];
} = {
  daily: [...PC_CONSOLE_ALL_GRANULARITIES_METRICS, ...PC_CONSOLE_DAILY_GRANULARITY_METRICS],
  weekly: [...PC_CONSOLE_ALL_GRANULARITIES_METRICS, ...PC_CONSOLE_WEEKLY_GRANULARITY_METRICS],
  monthly: [...PC_CONSOLE_ALL_GRANULARITIES_METRICS, ...PC_CONSOLE_MONTHLY_GRANULARITY_METRICS],
  yearly: [],
};

const aggregateCategoryMetrics = (
  categoryMetrics: any[],
): {
  map: {
    [key: string]: any;
  };
  options: OptionsItemType[];
} => {
  if (!Array.isArray(categoryMetrics)) {
    return {
      map: {},
      options: [],
    };
  }
  const options: OptionsItemType[] = [];
  const map: {
    [key: string]: any;
  } = {};
  for (const categoryMetric of categoryMetrics) {
    const metricKey = categoryMetric?.value;
    // displayCategory 只负责一级菜单展示；category 保留旧 namespace，供置灰、URL 和请求参数继续解析。
    const categoryKey = categoryMetric?.displayCategory || categoryMetric?.category;
    if (!metricKey) continue;
    if (!categoryKey || !categoryMetric?.category) continue;
    const metricDataSource = categoryMetric?.dataSource;
    const metricName = categoryMetric?.text;
    const optionKey = generateDataSourceMetricKey(categoryMetric.category, metricKey);
    const metricDisabledTipsLocaleKey = categoryMetric?.metricDisabledTipsLocaleKey;
    const metricDisabledTips = (InfoDoc as any)?.[metricDisabledTipsLocaleKey];
    const metricDisabledTipsCN = (InfoDocCN as any)?.[metricDisabledTipsLocaleKey];

    let option = options.find((item) => item?.value === categoryKey);
    if (!option) {
      const categoryDisabledTipsLocaleKey = categoryMetric?.categoryDisabledTipsLocaleKey;
      const categoryDisabledTips = (InfoDoc as any)?.[categoryDisabledTipsLocaleKey];
      const categoryDisabledTipsCN = (InfoDocCN as any)?.[categoryDisabledTipsLocaleKey];
      const displayCategoryFields = PC_CONSOLE_DISPLAY_CATEGORY_FIELDS?.[categoryKey] || {};
      // 级联组件的父级选项按 text/textCN 读取双语成品文案；配置层只保存 locale id，这里统一解析两种语言。
      const displayCategoryLocaleKey =
        categoryMetric?.displayCategoryLocaleKey || displayCategoryFields?.displayCategoryLocaleKey;
      const displayCategoryNameEN =
        (InfoDoc as any)?.[displayCategoryLocaleKey] || categoryMetric?.categoryNameEN;
      const displayCategoryNameCN =
        (InfoDocCN as any)?.[displayCategoryLocaleKey] || categoryMetric?.categoryNameCN;
      option = {
        value: categoryKey,
        text: displayCategoryNameEN,
        textCN: displayCategoryNameCN,
        bubbleContent:
          categoryDisabledTips || (InfoDoc as any)?.[displayCategoryFields?.categoryDisabledTipsLocaleKey],
        bubbleContentCN:
          categoryDisabledTipsCN ||
          (InfoDocCN as any)?.[displayCategoryFields?.categoryDisabledTipsLocaleKey],
        children: [],
      };
      options.push(option);
    }

    option.children?.push({
      value: optionKey,
      text: (InfoDoc as any)?.[metricName] || metricName,
      textCN: (InfoDocCN as any)?.[metricName] || metricName,
      bubbleContent: metricDisabledTips,
      bubbleContentCN: metricDisabledTipsCN,
      // @ts-ignore
      icon: getDataSourceIcon(categoryMetric?.icon ? categoryMetric?.icon : metricDataSource),
    });
    map[optionKey] = categoryMetric;
  }
  return {
    options,
    map,
  };
};

const databrainMetricsDisabledTipsLocaleKeysMap: Record<string, string> = {
  databrain__total_revenue: 'dbi_info_databrain_total_revenue_trends',
  databrain__lifetime_total_revenue: 'dbi_info_databrain_lifetime_total_revenue',
  databrain__base_game_sales_units: 'dbi_info_databrain_total_units_basegame',
  databrain__lifetime_base_game_sales_units: 'dbi_info_databrain_lifetime_total_units',
};

const databrainMetricsList = [
  {
    text: 'dbi_metric_total_revenue',
    value: 'databrain__total_revenue',
    dataSource: 'databrain',
    info: 'dbi_info_databrain_total_revenue_trends',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 260,
    // width_cn: 240,
    sumavgArr: [SUM, AVG],
    segType: segType.revenue,
    icon: 'databrainbluebg',
    category: 'revenue',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
  },
  {
    text: 'dbi_metric_lifetime_total_revenue',
    value: 'databrain__lifetime_total_revenue',
    dataSource: 'databrain',
    info: 'dbi_info_databrain_lifetime_total_revenue',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 280,
    // width_cn: 260,
    sumavgArr: [SUM, AVG],
    segType: segType.revenue,
    icon: 'databrainbluebg',
    category: 'revenue',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
  },
  {
    text: 'dbi_metric_base_game_sales_units',
    value: 'databrain__base_game_sales_units',
    dataSource: 'databrain',
    info: 'dbi_info_databrain_total_units_basegame',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 250,
    // width_cn: 240,
    sumavgArr: [SUM, AVG],
    segType: segType.revenue,
    icon: 'databrainbluebg',
    category: 'revenue',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
  },
  {
    text: 'dbi_metric_lifetime_base_game_sales_units',
    value: 'databrain__lifetime_base_game_sales_units',
    dataSource: 'databrain',
    info: 'dbi_info_databrain_lifetime_total_units',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 290,
    // width_cn: 270,
    sumavgArr: [SUM, AVG],
    segType: segType.revenue,
    icon: 'databrainbluebg',
    category: 'revenue',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
  },
];

const mscienceLifetimeMetrics = [
  {
    text: `dbi_metric_mscience_lifetime_digital_revenue`,
    value: 'mscienceLifetimeDigitalRevenue',
    dataSource: 'mscience',
    info: `dbi_info_mscience_lifetime_total_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 320,
    // width_cn: 300,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    tabName: 'digital_revenue',
  },
  {
    text: `dbi_metric_mscience_lifetime_digital_units`,
    value: 'mscienceLifetimeDigitalUnits',
    dataSource: 'mscience',
    info: `dbi_info_mscience_lifetime_units_new`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 320,
    // width_cn: 300,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    tabName: 'digital_revenue',
  },
];

const mscienceLifetimeMetricsDisabledTipsLocaleKeysMap: Record<string, string> = {
  mscienceLifetimeDigitalRevenue: 'dbi_info_mscience_lifetime_total_revenue',
  mscienceLifetimeDigitalUnits: 'dbi_info_mscience_lifetime_units_new',
};

const databrainMetricsCategoryList = appendDynamicFields2ListData(databrainMetricsList, (payload: any) => ({
  category: 'revenue',
  categoryNameEN: 'Revenue',
  categoryNameCN: '收入',
  categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
  metricDisabledTipsLocaleKey: databrainMetricsDisabledTipsLocaleKeysMap[payload?.value],
}));

const databrainCalibrationRevenueCategoryList = appendDynamicFields2ListData(
  mscienceLifetimeMetrics,
  (payload: any) => ({
    category: 'revenue',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey:
      mscienceLifetimeMetricsDisabledTipsLocaleKeysMap[payload?.value],
  }),
);

const pcConsoleDatabrainCategoryMetrics = [
  ...(databrainMetricsCategoryList as any[]),
  ...(databrainCalibrationRevenueCategoryList as any[]),
];
export const digitalRevenueList = [
  {
    // text: 'Digital Revenue($)',
    text: `dbi_metric_digitalRevenue`,
    value: 'digitalRevenue',
    dataSource: 'mscience',
    info: `dbi_info_sum_selected`,
    // infoDetail:'',//与info一样
    iconInfo: `dbi_info_digital_revenue_tip`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    defaultSelect: true,
    tabName: 'digital_revenue',
  },
  {
    // text: 'Units',
    text: `dbi_metric_digital_units`,
    value: 'units',
    dataSource: 'mscience',
    info: `dbi_info_units_indicates_basegame`,
    // infoDetail:'',//与info一样
    iconInfo: `dbi_info_units_sold`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    defaultSelect: true,
    tabName: 'digital_revenue',
  },
  {
    // text: 'Digital ARPPU($) (US only)',
    text: `dbi_metric_digitalARPPU`,
    value: 'digitalARPPU',
    dataSource: 'mscience',
    info: `dbi_info_arppu_usonly`,
    infoDetail: `dbi_info_arppu_usonly2`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 280,
    width_cn: 330,
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
  },
  {
    // text: 'AST',
    text: `dbi_metric_ast`,
    value: 'ast',
    dataSource: 'mscience',
    info: `dbi_info_ast_average`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 210,
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
  },
];
export const digitalRevenueListNew = [
  {
    text: `dbi_metric_mscience_lifetime_digital_revenue`,
    value: 'mscienceLifetimeDigitalRevenue',
    dataSource: 'mscience',
    info: `dbi_info_mscience_lifetime_total_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 320,
    // width_cn: 300,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    tabName: 'digital_revenue',
  },
  {
    text: `dbi_metric_mscience_lifetime_digital_units`,
    value: 'mscienceLifetimeDigitalUnits',
    dataSource: 'mscience',
    info: `dbi_info_mscience_lifetime_units_new`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 320,
    // width_cn: 300,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    tabName: 'digital_revenue',
  },
  {
    // text: 'Digital Revenue($)',
    text: `dbi_metric_digitalRevenue`,
    value: 'digitalRevenue',
    dataSource: 'mscience',
    info: `dbi_info_sum_selected`,
    // infoDetail:'',//与info一样
    iconInfo: `dbi_info_digital_revenue_tip`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    defaultSelect: true,
    tabName: 'digital_revenue',
  },
  {
    // text: 'Units',
    text: `dbi_metric_digital_units`,
    value: 'units',
    dataSource: 'mscience',
    info: `dbi_info_units_indicates_basegame`,
    // infoDetail:'',//与info一样
    iconInfo: `dbi_info_units_sold`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
    defaultSelect: true,
    tabName: 'digital_revenue',
  },
  {
    // text: 'Digital ARPPU($) (US only)',
    text: `dbi_metric_digitalARPPU`,
    value: 'digitalARPPU',
    dataSource: 'mscience',
    info: `dbi_info_arppu_usonly`,
    infoDetail: `dbi_info_arppu_usonly2`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 280,
    width_cn: 330,
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
  },
  {
    // text: 'AST',
    text: `dbi_metric_ast`,
    value: 'ast',
    dataSource: 'mscience',
    info: `dbi_info_ast_average`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 210,
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.mscience,
  },
];
export const metricsDigitalRevenueList = digitalRevenueList.filter((item) =>
  ['digitalRevenue', 'units'].includes(item.value),
);

export const npdList = [
  {
    // text: 'Physical Revenue($)',
    text: `dbi_metric_physical_revenue`,
    value: 'revenue',
    valueOther: 'physicalRevenue',
    dataSource: 'npd',
    info: `dbi_info_sum_selected`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 230,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.npd,
    defaultSelect: true,
    tabName: 'physical_revenue',
    regions: ['us'],
    segmentList: [],
  },
  {
    text: `dbi_metric_physical_units`,
    value: 'physical_units',
    dataSource: 'npd',
    info: `dbi_info_sum_selected`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.npd,
    defaultSelect: false,
    regions: ['us'],
    segmentList: [],
  },
];
export const pcuPageList = [
  {
    text: `dbi_metric_source_steam_pcu`,
    value: 'source_steam__pcu',
    dataSource: 'source_steam',
    icon: 'source_steam',
    info: `dbi_info_source_steam_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  },
  {
    text: `dbi_metric_source_steam_acu`,
    value: 'source_steam__acu',
    dataSource: 'source_steam',
    icon: 'source_steam',
    info: `dbi_info_source_steam_acu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  },
  {
    text: `dbi_metric_vgi_steam_followers`,
    value: 'steamfollowers',
    icon: 'steam',
    dataSource: 'steam',
    metricValue: 'steamcharts',
    info: `dbi_info_steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 210,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: false,
  },
  {
    text: `dbi_metric_alinea_steam_pcu`,
    value: 'alinea__pcu',
    dataSource: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  },
  {
    text: `dbi_metric_alinea_steam_acu`,
    value: 'alinea__acu',
    dataSource: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_acu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  },
  {
    text: `dbi_metric_alinea_steam_followers`,
    value: 'alinea__steamfollowers',
    dataSource: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea__steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
  },
  {
    text: `dbi_metric_alinea_steam_reviews`,
    value: 'alinea__steamreviews',
    dataSource: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea__steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
  },
  {
    text: `dbi_metric_gamalytic_steam_pcu`,
    value: 'gamalytic__pcu',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 180,
    // width_cn: 210,
  },
  {
    text: `dbi_metric_gamalytic_steam_acu`,
    value: 'gamalytic__acu',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 180,
    // width_cn: 200,
    metricKey: 'avg_players',
  },
  {
    text: `dbi_metric_gamalytic_steam_followers`,
    value: 'gamalytic__steamfollowers',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 210,
    segType: segType.activeuser,
    defaultSelect: true,
    dataSourceType: 'steamcharts',
  },
  {
    text: `dbi_metric_gamalytic_steam_reviews`,
    value: 'gamalytic__steamreviews',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 215,
    segType: segType.activeuser,
    defaultSelect: true,
    dataSourceType: 'steamcharts',
  },
  {
    text: `dbi_metric_vgi_steam_pcu`,
    value: 'pcu',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 270,
    // width_cn: 270,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: false,
  },
  {
    text: `dbi_metric_vgi_steam_acu`,
    value: 'acu',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 260,
    // width_cn: 260,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: false,
  },
  {
    text: `dbi_metric_vgi_steam_reviews`,
    value: 'steamreviews',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 215,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: false,
  },
];
// export const metricsTablePCUListOld = [
//   {
//     textStr: 'PCU',
//     text: `dbi_metric_pcu`,
//     value: 'pcu',
//     dataSource: 'steamcharts',
//     info: `dbi_info_pcu`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     width: 180,
//     width_cn: 210,
//     segType: segType.activeuser,
//     dataSourceType: dataSourceType.steamcharts,
//     defaultSelect: true,
//   },
//   {
//     textStr: 'ACU',
//     text: `dbi_metric_acu`,
//     value: 'acu',
//     dataSource: 'steamcharts',
//     info: `dbi_info_acu_avg`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     width: 180,
//     width_cn: 210,
//     segType: segType.activeuser,
//     dataSourceType: dataSourceType.steamcharts,
//     defaultSelect: true,
//   },
// ];

export const metricsTablePCUList = [
  {
    text: `dbi_metric_vgi_steam_pcu`,
    value: 'pcu',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: false,
  },
  {
    text: `dbi_metric_vgi_steam_acu`,
    value: 'acu',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: false,
  },
];
const steamReviews = [
  {
    text: `dbi_metric_vgi_steam_reviews`,
    value: 'steamreviews',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: false,
  },
]
const steamFollowers = [
  {
    text: `dbi_metric_vgi_steam_followers`,
    value: 'steamfollowers',
    icon: 'steam',
    dataSource: 'vginsights',
    bubbleDataSource: 'steam',
    metricValue: 'steamcharts',
    info: `dbi_info_steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 240,
    width_cn: 320,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: false,
  },
]

// const steamFollowersListOld = [
//   {
//     textStr: 'Steam Followers',
//     text: `dbi_metric_steam_followers`,
//     value: 'steamfollowers',
//     // dataSource: 'vginsights',
//     icon: 'steam',
//     dataSource: 'vginsights',
//     info: `dbi_info_steamfollowers`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     width: 210,
//     width_cn: 210,
//     segType: segType.activeuser,
//     dataSourceType: dataSourceType.steamcharts,
//     defaultSelect: true,
//   },
//   {
//     textStr: 'Steam Reviews',
//     text: `dbi_metric_steam_reviews`,
//     value: 'steamreviews',
//     icon: 'steam',
//     dataSource: 'vginsights',
//     info: `dbi_info_steamreviews`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     width: 210,
//     width_cn: 215,
//     segType: segType.activeuser,
//     dataSourceType: dataSourceType.steamcharts,
//     defaultSelect: true,
//   },
// ];

const steamSteamDataFollowersList = [
  {
    text: `dbi_metric_vgi_steam_followers`,
    value: 'steamfollowers',
    // dataSource: 'vginsights',
    icon: 'steam',
    dataSource: 'vginsights',
    bubbleDataSource: 'steam', // 气泡提示显示为 Steam 数据源
    // 详情页按 Steam 展示分组，真实请求数据源仍保持 VGI。
    displaySource: 'steam',
    info: `dbi_info_steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 210,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
  {
    text: `dbi_metric_vgi_steam_reviews`,
    value: 'steamreviews',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 215,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
];

export const sourceSteamFollowersList = steamSteamDataFollowersList.filter(
  item => item.value === 'steamfollowers'
);

// Steam Data子页面专用的Followers列表
export const steamFollowersListForSteamData = [
  {
    text: `dbi_metric_vgi_steam_followers`,
    value: 'steamfollowers',
    icon: 'steam',
    dataSource: 'vginsights',
    bubbleDataSource: 'steam', // 气泡提示显示为 Steam 数据源
    // 详情页按 Steam 展示分组，真实请求数据源仍保持 VGI。
    displaySource: 'steam',
    info: `dbi_info_steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 210,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
  {
    text: `dbi_metric_vgi_steam_reviews`,
    value: 'steamreviews',
    icon: 'vginsights',
    dataSource: 'vginsights',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 210,
    // width_cn: 215,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
];

// VGI Steam PCU/ACU 列表（带VGI前缀）
export const steamPCUList = [
  {
    text: 'dbi_metric_vgi_steam_pcu',
    value: 'pcu',
    dataSource: 'vginsights',
    icon: 'vginsights',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 180,
    // width_cn: 242,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
  },
  {
    text: 'dbi_metric_vgi_steam_acu',
    value: 'acu',
    dataSource: 'vginsights',
    icon: 'vginsights',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 180,
    // width_cn: 242,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
  },
];

// Steam Data子页面专用的PCU+Followers列表（带VGI前缀）
export const steamPCUFollowerListForSteamData = [...steamPCUList, ...steamFollowersListForSteamData];

export const sourceSteamPCUList = [
  {
    text: 'dbi_metric_source_steam_pcu',
    value: 'source_steam__pcu',
    dataSource: 'source_steam',
    icon: 'source_steam',
    info: `dbi_info_source_steam_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
  {
    text: 'dbi_metric_source_steam_acu',
    value: 'source_steam__acu',
    dataSource: 'source_steam',
    icon: 'source_steam',
    info: `dbi_info_source_steam_acu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
   dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
]

// Steam Data 子页面 PCU/ACU（source_steam 前缀）
export const sourceSteamList = [
  {
    text: 'dbi_metric_source_steam_pcu',
    value: 'source_steam__pcu',
    icon: 'source_steam',
    dataSource: 'source_steam',
    info: `dbi_info_source_steam_pcu`,
  },
  {
    text: 'dbi_metric_source_steam_acu',
    value: 'source_steam__acu',
    icon: 'source_steam',
    dataSource: 'source_steam',
    info: `dbi_info_source_steam_acu`,
  },
];
export const alineaPCUList = [
  {
    text: 'dbi_metric_alinea_steam_pcu',
    value: 'alinea__pcu',
    dataSource: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
  },
  {
    text: 'dbi_metric_alinea_steam_acu',
    value: 'alinea__acu',
    dataSource: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_acu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.steamcharts,
  },
]
// Gamalytic PCU list (pcu and acu with gamalytic__ prefix)
export const gamalyticPCUList = [
  {
    text: 'dbi_metric_gamalytic_steam_pcu',
    value: 'gamalytic__pcu',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    // 在 All Metrics 的 "Sources=Steam" 下展示，但请求分组仍按 dataSource=Gamalytic 走
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
  {
    text: 'dbi_metric_gamalytic_steam_acu',
    value: 'gamalytic__acu',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    // 在 All Metrics 的 "Sources=Steam" 下展示，但请求分组仍按 dataSource=Gamalytic 走
    dataSourceType: dataSourceType.steamcharts,
    defaultSelect: true,
  },
];
const alineaSteamFollowersDetail = {
  text: `dbi_metric_alinea_steam_followers`,
  value: 'alinea__steamfollowers',
  dataSource: 'alinea',
  icon: 'alinea',
  info: `dbi_info_alinea__steamfollowers`,
  renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  width: 220,
  segType: segType.activeuser,
  dataSourceType: dataSourceType.steamcharts,
};

const alineaSteamReviewsDetail = {
  text: `dbi_metric_alinea_steam_reviews`,
  value: 'alinea__steamreviews',
  dataSource: 'alinea',
  icon: 'alinea',
  info: `dbi_info_alinea__steamreviews`,
  renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  width: 220,
  segType: segType.activeuser,
  dataSourceType: dataSourceType.steamcharts,
};

export const alineaFollowersList = [
  alineaSteamFollowersDetail,
  alineaSteamReviewsDetail,
];
// Gamalytic Followers list (steamfollowers and steamreviews with gamalytic__ prefix)
const gamalyticSteamFollowersDetail = {
  text: `dbi_metric_gamalytic_steam_followers`,
  value: 'gamalytic__steamfollowers',
  dataSource: 'gamalytic',
  icon: 'gamalytic',
  info: `dbi_info_gamalytic__steamfollowers`,
  renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  width: 280,
  width_cn: 260,
  segType: segType.activeuser,
  // 在 All Metrics 的 "Sources=Steam" 下展示，但请求分组仍按 dataSource=Gamalytic 走
  dataSourceType: dataSourceType.steamcharts,
  defaultSelect: true,
};

const gamalyticSteamReviewsDetail = {
  text: `dbi_metric_gamalytic_steam_reviews`,
  value: 'gamalytic__steamreviews',
  dataSource: 'gamalytic',
  icon: 'gamalytic',
  info: `dbi_info_steamreviews`,
  renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  width: 260,
  width_cn: 260,
  segType: segType.activeuser,
  // 在 All Metrics 的 "Sources=Steam" 下展示，但请求分组仍按 dataSource=Gamalytic 走
  dataSourceType: dataSourceType.steamcharts,
  defaultSelect: true,
};

export const gamalyticFollowersList = [
  gamalyticSteamFollowersDetail,
  gamalyticSteamReviewsDetail,
];

// Steam 指标集合：VGI Steam + Gamalytic Steam（只增加 PCU/ACU/Followers/Reviews 这 4 个）
export const metricsTablePCUFollwerList = [
  ...sourceSteamPCUList,
  ...steamFollowers,
  ...alineaPCUList,
  ...alineaFollowersList,
  ...gamalyticPCUList,
  ...gamalyticFollowersList,
  ...metricsTablePCUList,
  ...steamReviews,
];

// export const metricsTablePCUFollwerListOld = [
//   ...metricsTablePCUListOld,
//   ...steamFollowersListOld,
// ];

// Gamalytic Sales Detail list (est_revenue, est_units, etc. with gamalytic__ prefix)
export const gamalyticSalesDetailList = [
  {
    text: `dbi_metric_gamalytic_steam_est_revenue`,
    value: 'gamalytic__est_revenue',
    info: `dbi_info_gamalytic_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    width: 360,
    width_cn: 300,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'line',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    text: `dbi_metric_gamalytic_steam_cumulative_premium_revenue`,
    value: 'gamalytic__est_cumulative_revenue',
    info: `dbi_info_gamalytic_est_cumulative_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 250,
    width: 240,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_est_units`,
    value: 'gamalytic__est_units',
    info: `dbi_info_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    width: 240,
    width_cn: 230,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'bar',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    text: `dbi_metric_gamalytic_steam_cumulative_units`,
    value: 'gamalytic__est_cumulative_units',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_steam_daily_wishlist`,
    value: 'gamalytic__wishlists',
    info: 'dbi_info_daily_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    newMore: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_steam_lifetime_wishlist`,
    value: 'gamalytic__wishlists_total',
    info: 'dbi_info_gamalytic__lifetime_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    newMore: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_est_price`,
    value: 'gamalytic__est_price',
    info: `dbi_info_est_price`,
    renderType: RenderType.STEAM_PRICE,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    width: 280,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_weighted_price`,
    value: 'gamalytic__weighted_price',
    info: `dbi_info_weighted_price`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 340,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_lifetime_weighted_price_trends`,
    value: 'gamalytic__lifetime_weighted_price',
    info: '',
    renderType: RenderType.PRICE,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 300,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_players_by_country`,
    metricName: 'Players by country',
    value: 'gamalytic__players_by_country',
    info: `dbi_info_players_by_country`,
    renderType: RenderType.COUNTRY_CHART,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    width: 360,
    dateEnum: ['daily'],
    chartMsg: {
      topDefault: 'bar',
      topDisableLine: true,
      topDisablePie: true,
      countryDefault: 'bar',
      countryDisableLine: true,
      countryDisablePie: true,
      statsDefault: 'bar',
    },
  },
  {
    text: `dbi_metric_gamalytic_playtime_steam`,
    value: 'gamalytic__playtime',
    displayCategory: 'engagement',
    metricName: 'Avg. Playtime',
    info: `dbi_info_gamalytic__playtime`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    // width: 380,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      topDisableLine: true,
      countryDefault: 'line',
      countryDisableLine: true,
      statsDefault: 'line',
    },
  },
];
export const gamalyticSalesList = gamalyticSalesDetailList.filter(
  (t) =>
    t.value !== 'gamalytic__est_cumulative_revenue' &&
    t.value !== 'gamalytic__est_cumulative_units' &&
    t.value !== 'gamalytic__lifetime_weighted_price' &&
    t.value !== 'gamalytic__wishlists' &&
    t.value !== 'gamalytic__wishlists_total',
);

export const steamPCUFollowerList = [...steamPCUList, ...steamSteamDataFollowersList];
export const newZooRankList = [
  {
    // text: 'Rank',
    text: `dbi_metric_rank`,
    value: 'Rank',
    renderType: RenderType.RANK_AND_RATIO,
    dataSource: 'newzoo',
    iconInfo: `Here shows ranks of the game on the top MAU chart.`,
    // width: 150,
  },
];

export const mauList = [
  {
    // text: 'MAU',
    text: `dbi_metric_mau`,
    value: 'mau',
    info: `dbi_info_mau_represents`,
    // info: `Represents the number of users who launched a title at least once in a given month. MAU指在给定月份中至少启动过一次游戏的用户数量。`,
    infoDetail: `dbi_info_average_mau`,
    renderType: RenderType.VALUE_AND_RATIO,
    dataSource: 'newzoo',
    iconInfo: `dbi_info_mau_rank`,
    // width: 190,
  },
  {
    // text: 'Acquisition %',
    text: `dbi_metric_acquisition`,
    value: 'acquisition',
    infoDetail: `dbi_info_acquisition_detail`,
    renderType: RenderType.GROWTH_AND_RATIO,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    // width: 200,
  },
];
export const lifetimePlayerList = [
  {
    // text: 'Lifetime Players',
    text: `dbi_metric_lifetime_player`,
    value: 'lifetime_player',
    info: `Total unique users that have ever played the title up until the specified time period. 至少玩过该游戏一次的总玩家数量。`,
    renderType: RenderType.VALUE_AND_RATIO,
    dataSource: 'newzoo',
    // width: 220,
  },
];
export const newzooMAUList = [
  {
    // text: 'MAU',
    text: `dbi_metric_mau`,
    value: 'mau',
    info: `dbi_info_mau_represents`,
    // info: `Represents the number of users who launched a title at least once in a given month. MAU指在给定月份中至少启动过一次游戏的用户数量。`,
    infoDetail: `dbi_info_average_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'newzoo',
    iconInfo: `dbi_info_mau_rank`,
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.newzoo,
    defaultSelect: true,
  },
  {
    // text: 'Player Share',
    text: `dbi_metric_player_share`,
    value: 'player_share',
    info: `dbi_info_player_share`,
    infoDetail: `dbi_info_weighted_avg`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    width: 200,
    width_cn: 200,
    sumavgArr: [],
    hasByDevice: false,
    metricValue: 'newzoo_weight',
    segType: segType.activeuser,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'MAU Growth',
    text: `dbi_metric_mau_growth`,
    value: 'mau_growth',
    info: `dbi_info_mau_growth`,
    infoDetail: `dbi_info_mau_growth_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'MAU - New',
    text: `dbi_metric_mau_new`,
    value: 'mau_new',
    info: `dbi_info_mau_new`,
    infoDetail: `dbi_info_mau_new_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'MAU - Returning',
    text: `dbi_metric_mau_returning`,
    value: 'mau_returning',
    info: `dbi_info_mau_returning`,
    infoDetail: `dbi_info_mau_returning_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    width: 210,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.newzoo,
  },
];
export const newzooEngagementList = [
  {
    // text: 'Lifetime Players',
    text: `dbi_metric_lifetime_player`,
    value: 'lifetime_player',
    info: `dbi_info_lifetime_player`,
    infoDetail: `dbi_info_lifetime_player_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    width: 200,
    width_cn: 200,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.newzoo,
  },
  // Engagement
  {
    // text: 'Avg Daily Playtime',
    text: `dbi_metric_avg_daily_playtime`,
    textDetail: `dbi_metric_avg_daily_playtime_min`,
    value: 'avg_daily_playtime',
    info: `dbi_info_avg_daily_playtime`,
    infoDetail: `dbi_info_avg_daily_playtime_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 270,
    width_cn: 240,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Avg Daily Sessions',
    text: `dbi_metric_avg_daily_sessions`,
    textDetail: `dbi_metric_avg_daily_sessions2`,
    value: 'avg_daily_sessions',
    info: `dbi_info_avg_daily_sessions`,
    infoDetail: `dbi_info_avg_daily_sessions_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 220,
    width_cn: 200,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Avg Days Played',
    text: `dbi_metric_avg_days_played`,
    textDetail: `dbi_metric_avg_days_played2`,
    value: 'avg_days_played',
    info: `dbi_info_avg_days_played`,
    infoDetail: `dbi_info_avg_days_played_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 210,
    width_cn: 200,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Avg Monthly Playtime',
    text: `dbi_metric_avg_monthly_playtime`,
    textDetail: `dbi_metric_avg_monthly_playtime_hour`,
    value: 'avg_monthly_playtime',
    info: `dbi_info_avg_monthly_playtime`,
    infoDetail: `dbi_info_avg_monthly_playtime_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 300,
    width_cn: 240,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Avg Monthly Sessions',
    text: `dbi_metric_avg_monthly_sessions`,
    textDetail: `dbi_metric_avg_monthly_sessions2`,
    value: 'avg_monthly_sessions',
    info: `dbi_info_avg_monthly_sessions`,
    infoDetail: `dbi_info_avg_monthly_sessions_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 250,
    width_cn: 200,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Avg Playtime Per Sessions',
    text: `dbi_metric_avg_playtime_per_session`,
    textDetail: `dbi_metric_avg_playtime_per_session_min`,
    value: 'avg_playtime_per_session',
    info: `dbi_info_avg_playtime_per_session`,
    infoDetail: `dbi_info_avg_playtime_per_session_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 315,
    width_cn: 270,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Stickiness',
    text: `dbi_metric_stickiness`,
    value: 'stickiness',
    info: `dbi_info_stickiness`,
    infoDetail: `dbi_info_stickiness_detail`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 200,
    width_cn: 200,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Total Hours',
    text: `dbi_metric_total_hours`,
    value: 'total_hours',
    info: `dbi_info_total_hours`,
    infoDetail: `dbi_info_total_hours_detail`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 200,
    width_cn: 200,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.engagement,
    dataSourceType: dataSourceType.newzoo,
  },
];
export const newzooChurnAcquisitionList = [
  {
    // text: 'Churn %',
    text: `dbi_metric_churn`,
    value: 'churn',
    info: `dbi_info_churn`,
    infoDetail: `dbi_info_churn_detail`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 200,
    width_cn: 200,
    segType: segType.churnacq,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Acquisition %',
    text: `dbi_metric_acquisition`,
    value: 'acquisition',
    info: `dbi_info_acquisition`,
    infoDetail: `dbi_info_acquisition_detail`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 200,
    width_cn: 200,
    segType: segType.churnacq,
    dataSourceType: dataSourceType.newzoo,
  },
];
export const newzooTimeSpentList = [
  {
    // text: 'Time Spent 0-1 hrs',
    text: `dbi_metric_time_spent_0H`,
    value: 'time_spent_0H',
    info: `dbi_info_timespent_01`,
    infoDetail: `dbi_info_timespent_detail_01`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 230,
    width_cn: 210,
    segType: segType.timeSpent,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Time Spent 1-5 hrs',
    text: `dbi_metric_time_spent_1H`,
    value: 'time_spent_1H',
    info: `dbi_info_timespent_15`,
    infoDetail: `dbi_info_timespent_detail_15`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 230,
    width_cn: 210,
    segType: segType.timeSpent,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Time Spent 5-10 hrs',
    text: `dbi_metric_time_spent_5H`,
    value: 'time_spent_5H',
    info: `dbi_info_timespent_510`,
    infoDetail: `dbi_info_timespent_detail_510`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 230,
    width_cn: 210,
    segType: segType.timeSpent,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Time Spent 10-25 hrs',
    text: `dbi_metric_time_spent_10H`,
    value: 'time_spent_10H',
    info: `dbi_info_timespent_1025`,
    infoDetail: `dbi_info_timespent_detail_1025`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 240,
    width_cn: 220,
    segType: segType.timeSpent,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Time Spent 25-50 hrs',
    text: `dbi_metric_time_spent_25H`,
    value: 'time_spent_25H',
    info: `dbi_info_timespent_2550`,
    infoDetail: `dbi_info_timespent_detail_2550`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 240,
    width_cn: 220,
    segType: segType.timeSpent,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    // text: 'Time Spent > 50 hrs',
    text: `dbi_metric_time_spent_50H`,
    value: 'time_spent_50H',
    info: `dbi_info_time_spent_50H`,
    infoDetail: `dbi_info_time_spent_50H_detail`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    width: 235,
    width_cn: 210,
    segType: segType.timeSpent,
    dataSourceType: dataSourceType.newzoo,
  },
];
export const newzooRetentionList = [
  {
    text: 'D1',
    textDetail: `dbi_retention_d1`,
    value: 'retention_d1',
    info: `dbi_info_retention_d1`,
    infoDetail: `dbi_info_retention_detail_d1`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    globalOnly: true,
    width: 200,
    width_cn: 200,
    segType: segType.retention,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    text: 'D2',
    textDetail: `dbi_retention_d2`,
    value: 'retention_d2',
    info: `dbi_info_retention_d2`,
    infoDetail: `dbi_info_retention_detail_d2`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    globalOnly: true,
    width: 200,
    width_cn: 200,
    segType: segType.retention,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    text: 'D4',
    textDetail: `dbi_retention_d4`,
    value: 'retention_d4',
    info: `dbi_info_retention_d4`,
    infoDetail: `dbi_info_retention_detail_d4`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    globalOnly: true,
    width: 200,
    width_cn: 200,
    segType: segType.retention,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    text: 'D7',
    textDetail: `dbi_retention_d7`,
    value: 'retention_d7',
    info: `dbi_info_retention_d7`,
    infoDetail: `dbi_info_retention_detail_d7`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    globalOnly: true,
    width: 200,
    width_cn: 200,
    segType: segType.retention,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    text: 'D14',
    textDetail: `dbi_retention_d14`,
    value: 'retention_d14',
    info: `dbi_info_retention_d14`,
    infoDetail: `dbi_info_retention_detail_d14`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    globalOnly: true,
    width: 200,
    width_cn: 200,
    segType: segType.retention,
    dataSourceType: dataSourceType.newzoo,
  },
  {
    text: 'D28',
    textDetail: `dbi_retention_d28`,
    value: 'retention_d28',
    info: `dbi_info_retention_d28`,
    infoDetail: `dbi_info_retention_detail_d28`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'newzoo',
    metricValue: 'newzoo_weight',
    globalOnly: true,
    width: 200,
    width_cn: 200,
    segType: segType.retention,
    dataSourceType: dataSourceType.newzoo,
  },
];

export const ampereMAUList = [
  {
    text: `dbi_metric_mau`,
    value: 'ampere_mau',
    info: `dbi_info_ampere_mau`,
    infoDetail: `dbi_info_ampere_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 190,
    // width_cn: 180,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_previous_mau`,
    value: 'ampere_previous_mau',
    info: `dbi_info_ampere_previous_mau`,
    infoDetail: `dbi_info_ampere_previous_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_added_mau`,
    value: 'ampere_added_mau',
    info: `dbi_metric_ampere_added_mau`,
    infoDetail: `dbi_metric_ampere_added_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_new_mau`,
    value: 'ampere_new_mau',
    info: `dbi_metric_ampere_new_mau`,
    infoDetail: `dbi_metric_ampere_new_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_retained_mau`,
    value: 'ampere_retained_mau',
    info: `dbi_metric_ampere_retained_mau`,
    infoDetail: `dbi_metric_ampere_retained_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_churned_mau`,
    value: 'ampere_churned_mau',
    info: `dbi_metric_ampere_churned_mau`,
    infoDetail: `dbi_metric_ampere_churned_mau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
];

export const ampereDAUList = [
  {
    text: `dbi_dau`,
    value: 'ampere_dau',
    info: `dbi_metric_ampere_dau`,
    infoDetail: `dbi_metric_ampere_dau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_new_dau`,
    value: 'ampere_new_dau',
    info: `dbi_metric_ampere_new_dau`,
    infoDetail: `dbi_metric_ampere_new_dau`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: true,
    segType: segType.activeuser,
    isNoMore: true,
    newMore: true,
  },
];

export const ampereEngagementList = [
  {
    text: `dbi_metric_avg_monthly_playtime`,
    value: 'ampere_avg_monthly_playtime',
    info: `dbi_metric_ampere_avg_monthly_playtime`,
    infoDetail: `dbi_metric_ampere_avg_monthly_playtime`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    metricValue: 'ampere',
    // width: 300,
    // width_cn: 245,
    segType: segType.engagement,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_acquisition_rate`,
    value: 'ampere_acquisition_rate',
    info: `dbi_metric_ampere_acquisition_rate`,
    infoDetail: `dbi_metric_ampere_acquisition_rate`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    metricValue: 'ampere',
    // width: 270,
    // width_cn: 245,
    segType: segType.engagement,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_stickiness`,
    value: 'ampere_stickiness',
    info: `dbi_metric_ampere_stickiness`,
    infoDetail: `dbi_metric_ampere_stickiness`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    metricValue: 'ampere',
    // width: 165,
    // width_cn: 145,
    segType: segType.engagement,
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_lifetime_player`,
    value: 'ampere_lifetime_users',
    info: `dbi_metric_ampere_lifetime_users`,
    infoDetail: `dbi_metric_ampere_lifetime_users`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_avg_dailly_playtime_hour`,
    value: 'ampere_avg_dailly_playtime',
    info: `dbi_metric_ampere_avg_dailly_playtime`,
    infoDetail: `dbi_metric_ampere_avg_dailly_playtime`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  // VGI 数据源的两个指标 metric 在 engagement 下
  // {
  //   text: 'dbi_metric_daily_wishlist',
  //   value: 'wishlists',
  //   info: 'dbi_info_daily_wishlist',
  //   infoDetail: 'dbi_info_daily_wishlist',
  //   renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  //   dataSource: 'vginsights',
  //   width: 210,
  //   width_cn: 215,
  //   sumavgArr: [],
  //   isNoMore: true,
  //   newMore: true,
  // },
  // {
  //   text: `dbi_metric_lifetime_wishlist`,
  //   value: 'wishlists_total',
  //   info: 'dbi_info_lifetime_wishlist',
  //   infoDetail: `dbi_info_lifetime_wishlist`,
  //   renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  //   dataSource: 'vginsights',
  //   width: 210,
  //   width_cn: 180,
  //   sumavgArr: [],
  //   isNoMore: true,
  //   newMore: true,
  // },
];

export const ampereRetentionList = [
  {
    // text: `D1`,
    text: `dbi_metric_ampere_d1`,
    value: 'ampere_d1',
    info: `dbi_metric_info_ampere_d1`,
    infoDetail: `dbi_metric_info_ampere_d1`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    // text: `D7`,
    text: `dbi_metric_ampere_d7`,
    value: 'ampere_d7',
    info: `dbi_metric_info_ampere_d7`,
    infoDetail: `dbi_metric_info_ampere_d7`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    // text: `D14`,
    text: `dbi_metric_ampere_d14`,
    value: 'ampere_d14',
    info: `dbi_metric_info_ampere_d14`,
    infoDetail: `dbi_metric_info_ampere_d14`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    // text: `D28`,
    text: `dbi_metric_ampere_d28`,
    value: 'ampere_d28',
    info: `dbi_metric_info_ampere_d28`,
    infoDetail: `dbi_metric_info_ampere_d28`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    // text: `D60`,
    text: `dbi_metric_ampere_d60`,
    value: 'ampere_d60',
    info: `dbi_metric_info_ampere_d60`,
    infoDetail: `dbi_metric_info_ampere_d60`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_rolling_d1`,
    value: 'ampere_rolling_d1',
    info: `dbi_metric_ampere_rolling_d1`,
    infoDetail: `dbi_metric_ampere_rolling_d1`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_rolling_d7`,
    value: 'ampere_rolling_d7',
    info: `dbi_metric_ampere_rolling_d7`,
    infoDetail: `dbi_metric_ampere_rolling_d7`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_rolling_d14`,
    value: 'ampere_rolling_d14',
    info: `dbi_metric_ampere_rolling_d14`,
    infoDetail: `dbi_metric_ampere_rolling_d14`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_rolling_d28`,
    value: 'ampere_rolling_d28',
    info: `dbi_metric_ampere_rolling_d28`,
    infoDetail: `dbi_metric_ampere_rolling_d28`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_rolling_d60`,
    value: 'ampere_rolling_d60',
    info: `dbi_metric_ampere_rolling_d60`,
    infoDetail: `dbi_metric_ampere_rolling_d60`,
    renderType: RenderType.GROWTH_ONLY,
    dataSource: 'ampere',
    // width: 210,
    // width_cn: 180,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
];
// export const newzooNoUpdateList = [
//   ...newzooMAUList,
//   ...newzooEngagementList,
//   ...newzooChurnAcquisitionList,
//   ...newzooTimeSpentList,
// ];

export const newzooList = [
  ...newzooMAUList,
  ...newzooEngagementList,
  // Churn %/Acquisition %
  ...newzooChurnAcquisitionList,
  // Time Spent
  ...newzooTimeSpentList,
  // Retention
  ...newzooRetentionList,
];

export const newzooMetricsDetail = newzooList.map((item) => ({
  text: /* item?.textDetail ?? */ item?.text,
  value: item?.value,
}));

export const steamSalesDetailList = [
  {
    // text: 'Premium Revenue($)', //表格头部
    text: `dbi_metric_vgi_est_revenue`,
    value: 'est_revenue',
    info: `dbi_info_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 250,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'line',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    // text: 'Cumulative Revenue($)', //表格头部
    text: `dbi_metric_vgi_cumulative_revenue`,
    value: 'est_cumulative_revenue',
    info: `dbi_info_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // text: 'Units',
    text: `dbi_metric_vgi_units`,
    value: 'est_units',
    info: `dbi_info_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 180,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'line',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    // text: 'Cumulative Units',
    text: `dbi_metric_vgi_cumulative_units`,
    value: 'est_cumulative_units',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // 'Daily Wishlist',
    text: `dbi_metric_vgi_daily_wishlist`,
    value: 'wishlists',
    info: 'dbi_info_daily_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    newMore: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // 'Daily Wishlist',
    text: `dbi_metric_vgi_lifetime_wishlist`,
    value: 'wishlists_total',
    info: 'dbi_info_lifetime_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 250,
    globalOnly: true,
    ifDetailOnly: true,
    newMore: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // text: 'Price($)',
    text: `dbi_metric_vgi_price`,
    value: 'est_price',
    info: `dbi_info_est_price`,
    renderType: RenderType.STEAM_PRICE,
    dataSource: 'vginsights',
    // width: 280,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // text: 'Weighted Price($)',
    text: `dbi_metric_vgi_weighted_price`,
    value: 'weighted_price',
    info: `dbi_info_weighted_price`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 230,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line', // 三态都有
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // text: 'Lifetime weighted price($)',
    text: `dbi_metric_vgi_lifetime_weighted_price`,
    value: 'lifetime_weighted_price',
    info: '',
    renderType: RenderType.PRICE,
    dataSource: 'vginsights',
    // width: 280,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line', // 三态都有
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    // text: 'Players by country',
    text: `dbi_metric_vgi_players_by_country`,
    metricName: 'Players by country',
    value: 'players_by_country',
    info: `dbi_info_players_by_country`,
    renderType: RenderType.COUNTRY_CHART,
    dataSource: 'vginsights',
    width: 360,
    dateEnum: ['daily'],
    chartMsg: {
      topDefault: 'bar', // 榜单默认堆叠，详情Country默认单柱
      topDisableLine: true,
      topDisablePie: true,
      countryDefault: 'bar',
      countryDisableLine: true,
      countryDisablePie: true,
      statsDefault: 'bar',
    },
  },
  {
    // text: 'Avg. Playtime(mins)',
    text: `dbi_metric_vgi_playtime`,
    value: 'playtime',
    displayCategory: 'engagement',
    metricName: 'Avg. Playtime',
    info: `dbi_info_vgi_playtime`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'vginsights',
    // width: 250,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      topDisableLine: true,
      countryDefault: 'line',
      countryDisableLine: true,
      statsDefault: 'line',
    },
  },
];

// export const steamSalesDetailListOld = [
//   {
//     // text: 'Premium Revenue($)', //表格头部
//     text: `dbi_metric_est_revenue`,
//     value: 'est_revenue',
//     info: `dbi_info_est_revenue`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 250,
//     globalOnly: true,
//     chartMsg: {
//       topDefault: 'bar',
//       countryDefault: 'bar',
//       statsDefault: 'line',
//     },
//     sumavgArr: [SUM, AVG],
//     hasByDevice: false,
//   },
//   {
//     // text: 'Cumulative Revenue($)', //表格头部
//     text: `dbi_metric_cumulative_revenue`,
//     value: 'est_cumulative_revenue',
//     info: `dbi_info_est_revenue`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 250,
//     globalOnly: true,
//     ifDetailOnly: true,
//     chartMsg: {
//       topDefault: 'line',
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // text: 'Units',
//     text: `dbi_metric_units`,
//     value: 'est_units',
//     info: `dbi_info_est_units`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 180,
//     globalOnly: true,
//     chartMsg: {
//       topDefault: 'bar',
//       countryDefault: 'bar',
//       statsDefault: 'line',
//     },
//     sumavgArr: [SUM, AVG],
//     hasByDevice: false,
//   },
//   {
//     // text: 'Cumulative Units',
//     text: `dbi_metric_est_cumulative_units`,
//     value: 'est_cumulative_units',
//     info: '',
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 250,
//     globalOnly: true,
//     ifDetailOnly: true,
//     chartMsg: {
//       topDefault: 'line',
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // 'Daily Wishlist',
//     text: `dbi_metric_daily_wishlist`,
//     value: 'wishlists',
//     info: 'dbi_info_daily_wishlist',
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 250,
//     globalOnly: true,
//     ifDetailOnly: true,
//     newMore: true,
//     chartMsg: {
//       topDefault: 'line',
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // 'Daily Wishlist',
//     text: `dbi_metric_lifetime_wishlist`,
//     value: 'wishlists_total',
//     info: 'dbi_info_lifetime_wishlist',
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 250,
//     globalOnly: true,
//     ifDetailOnly: true,
//     newMore: true,
//     chartMsg: {
//       topDefault: 'line',
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // text: 'Price($)',
//     text: `dbi_metric_jptb_price`,
//     value: 'est_price',
//     info: `dbi_info_est_price`,
//     renderType: RenderType.STEAM_PRICE,
//     dataSource: 'vginsights',
//     width: 280,
//     globalOnly: true,
//     chartMsg: {
//       topDefault: 'line',
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // text: 'Weighted Price($)',
//     text: `dbi_metric_weighted_price`,
//     value: 'weighted_price',
//     info: `dbi_info_weighted_price`,
//     renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
//     dataSource: 'vginsights',
//     width: 230,
//     globalOnly: true,
//     chartMsg: {
//       topDefault: 'line', // 三态都有
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // text: 'Lifetime weighted price($)',
//     text: `dbi_metric_lifetime_weighted_price`,
//     value: 'lifetime_weighted_price',
//     info: '',
//     renderType: RenderType.PRICE,
//     dataSource: 'vginsights',
//     width: 280,
//     globalOnly: true,
//     ifDetailOnly: true,
//     chartMsg: {
//       topDefault: 'line', // 三态都有
//       countryDefault: 'line',
//       statsDefault: 'line',
//     },
//   },
//   {
//     // text: 'Players by country',
//     text: `dbi_metric_players_by_country`,
//     metricName: 'Players by country',
//     value: 'players_by_country',
//     info: `dbi_info_players_by_country`,
//     renderType: RenderType.COUNTRY_CHART,
//     dataSource: 'vginsights',
//     width: 360,
//     chartMsg: {
//       topDefault: 'bar', // 榜单默认堆叠，详情Country默认单柱
//       topDisableLine: true,
//       topDisablePie: true,
//       countryDefault: 'bar',
//       countryDisableLine: true,
//       countryDisablePie: true,
//       statsDefault: 'bar',
//     },
//   },
//   {
//     // text: 'Avg. Playtime(mins)',
//     text: `dbi_metric_playtime`,
//     value: 'playtime',
//     metricName: 'Avg. Playtime',
//     info: `dbi_info_playtime`,
//     renderType: RenderType.VALUE_ONLY,
//     dataSource: 'vginsights',
//     width: 250,
//     globalOnly: true,
//     chartMsg: {
//       topDefault: 'bar',
//       topDisableLine: true,
//       countryDefault: 'line',
//       countryDisableLine: true,
//       statsDefault: 'line',
//     },
//   },
//   {
//     // text: 'Total followers',
//     text: `dbi_metric_followers`,
//     value: 'followers',
//     metricName: 'Total followers',
//     info: `dbi_info_followers`,
//     renderType: RenderType.VALUE_ONLY,
//     dataSource: 'vginsights',
//     width: 200,
//     globalOnly: true,
//     chartMsg: {
//       topDefault: 'bar',
//       topDisableLine: true,
//       countryDefault: 'line',
//       countryDisableLine: true,
//       statsDefault: 'line',
//     },
//   },
// ];

export const steamSalesList = steamSalesDetailList.filter(
  (t) =>
    t.value !== 'est_cumulative_revenue' &&
    t.value !== 'est_cumulative_units' &&
    t.value !== 'lifetime_weighted_price' &&
    t.value !== 'wishlists' &&
    t.value !== 'wishlists_total',
);

// SteamSales 页面：Gamalytic 指标集合（与 VGI SteamSalesList 过滤规则对齐）
export const gamalyticSteamSalesList = gamalyticSalesDetailList.filter(
  (t) =>
    t.value !== 'gamalytic__est_cumulative_revenue' &&
    t.value !== 'gamalytic__est_cumulative_units' &&
    t.value !== 'gamalytic__lifetime_weighted_price' &&
    t.value !== 'gamalytic__wishlists' &&
    t.value !== 'gamalytic__wishlists_total',
);

const steamSalesTotalFollowersColumn = {
  // text: 'Total followers',
  text: `dbi_metric_vgi_followers`,
  value: 'followers',
  metricName: 'Total followers',
  info: `dbi_info_vgi_followers`,
  renderType: RenderType.VALUE_ONLY,
  dataSource: 'vginsights',
  width: 300,
  globalOnly: true,
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    countryDefault: 'line',
    countryDisableLine: true,
    statsDefault: 'line',
  },
};

const gamalyticSteamFollowersColumn = {
  text: `dbi_metric_gamalytic_steam_followers`,
  value: 'gamalytic__steamfollowers',
  metricName: 'Gamalytic Steam Followers',
  dataSource: 'gamalytic',
  icon: 'gamalytic',
  info: `dbi_info_gamalytic__steamfollowers`,
  renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  width: 250,
  width_cn: 250,
  globalOnly: true,
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    countryDefault: 'line',
    countryDisableLine: true,
    statsDefault: 'line',
  },
};

/**
 * SteamSales 页面需要展示 Total Followers，但不影响其他复用 steamSalesList 的页面。
 */
export const steamSalesVgiListForSteamSalesPage = (() => {
  // 仅调整 VGI 区块：插入 Total Followers，并保持在 VGI playtime 后面
  const base = [...steamSalesList];
  const playtimeIndex = base.findIndex((t) => t.value === 'playtime');
  if (playtimeIndex === -1) return [...base, steamSalesTotalFollowersColumn];
  return [
    ...base.slice(0, playtimeIndex + 1),
    steamSalesTotalFollowersColumn,
    ...base.slice(playtimeIndex + 1),
  ];
})();

/**
 * SteamSales 页面需要展示 Gamalytic Steam Followers，但不影响其他复用 gamalyticSalesList 的页面。
 */
export const gamalyticSteamSalesListForSteamSalesPage = (() => {
  // 仅调整 Gamalytic 区块：插入 Steam Followers，并保持在 Gamalytic playtime 后面
  const base = [...gamalyticSteamSalesList];
  const playtimeIndex = base.findIndex((t) => t.value === 'gamalytic__playtime');
  if (playtimeIndex === -1) return [...base, gamalyticSteamFollowersColumn];
  return [
    ...base.slice(0, playtimeIndex + 1),
    gamalyticSteamFollowersColumn,
    ...base.slice(playtimeIndex + 1),
  ];
})();

/**
 * Stats 详情页：Alinea 指标全集
 * - 顺序、字段名（去掉前缀后）与 Gamalytic 完全一致
 * - 用于 GamesDetails Stats 子分段，保证 Alinea 与 Gamalytic 子项数量、命名一致
 */
const alineaSalesBaseDetailList = [
  {
    text: `dbi_metric_alinea_steam_est_revenue`,
    value: 'alinea__est_revenue',
    info: `dbi_info_alinea_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 240,
    width_cn: 240,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'line',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    text: `dbi_metric_alinea_steam_cumulative_premium_revenue`,
    value: 'alinea__est_cumulative_revenue',
    info: `dbi_info_alinea_est_cumulative_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 240,
    width_cn: 240,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_est_units`,
    value: 'alinea__est_units',
    info: `dbi_info_alinea_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 220,
    width_cn: 220,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'bar',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    text: `dbi_metric_alinea_steam_cumulative_units`,
    value: 'alinea__est_cumulative_units',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 240,
    width_cn: 240,
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_steam_daily_wishlist`,
    value: 'alinea__wishlists',
    info: 'dbi_info_daily_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    globalOnly: true,
    ifDetailOnly: true,
    newMore: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_steam_lifetime_wishlist`,
    value: 'alinea__wishlists_total',
    info: 'dbi_info_alinea__lifetime_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    globalOnly: true,
    ifDetailOnly: true,
    newMore: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_est_price`,
    value: 'alinea__est_price',
    info: `dbi_info_alinea_est_price`,
    renderType: RenderType.STEAM_PRICE,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 280,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_weighted_price`,
    value: 'alinea__weighted_price',
    info: `dbi_info_weighted_price`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_lifetime_weighted_price_trends`,
    value: 'alinea__lifetime_weighted_price',
    info: '',
    renderType: RenderType.PRICE,
    dataSource: 'alinea',
    icon: 'alinea',
    globalOnly: true,
    ifDetailOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
];

const alineaPlayersByCountryDetail = {
  text: `dbi_metric_alinea_players_by_country`,
  metricName: 'Players by country',
  value: 'alinea__players_by_country',
  info: `dbi_info_alinea__players_by_country`,
  renderType: RenderType.COUNTRY_CHART,
  dataSource: 'alinea',
  icon: 'alinea',
  width: 360,
    dateEnum: ['daily'],
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    topDisablePie: true,
    countryDefault: 'bar',
    countryDisableLine: true,
    countryDisablePie: true,
    statsDefault: 'bar',
  },
};

const alineaPlaytimeDetail = {
  text: `dbi_metric_alinea_playtime_steam`,
  value: 'alinea__playtime',
  metricName: 'Avg. Playtime',
  info: `dbi_info_alinea__playtime`,
  renderType: RenderType.VALUE_ONLY,
  dataSource: 'alinea',
  icon: 'alinea',
  globalOnly: true,
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    countryDefault: 'line',
    countryDisableLine: true,
    statsDefault: 'line',
  },
};

export const alineaSalesDetailList = [
  ...alineaSalesBaseDetailList,
  alineaPlayersByCountryDetail,
  alineaPlaytimeDetail,
];

// SteamSales 页面：Alinea 指标集合（与 gamalyticSteamSalesList 对齐的镜像列）
export const alineaSteamSalesList = [
  {
    text: `dbi_metric_alinea_steam_est_revenue`,
    value: 'alinea__est_revenue',
    info: `dbi_info_alinea_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 240,
    width_cn: 240,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'line',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    text: `dbi_metric_alinea_est_units`,
    value: 'alinea__est_units',
    info: `dbi_info_alinea_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 220,
    width_cn: 220,
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'bar',
    },
    sumavgArr: [SUM, AVG],
    hasByDevice: false,
  },
  {
    text: `dbi_metric_alinea_est_price`,
    value: 'alinea__est_price',
    info: `dbi_info_alinea_est_price`,
    renderType: RenderType.STEAM_PRICE,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 280,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_weighted_price`,
    value: 'alinea__weighted_price',
    info: `dbi_info_weighted_price`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'alinea',
    icon: 'alinea',
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_players_by_country`,
    metricName: 'Players by country',
    value: 'alinea__players_by_country',
    info: `dbi_info_alinea__players_by_country`,
    renderType: RenderType.COUNTRY_CHART,
    dataSource: 'alinea',
    icon: 'alinea',
    width: 360,
    dateEnum: ['daily'],
    chartMsg: {
      topDefault: 'bar',
      topDisableLine: true,
      topDisablePie: true,
      countryDefault: 'bar',
      countryDisableLine: true,
      countryDisablePie: true,
      statsDefault: 'bar',
    },
  },
  {
    text: `dbi_metric_alinea_playtime_steam`,
    value: 'alinea__playtime',
    metricName: 'Avg. Playtime',
    info: `dbi_info_alinea__playtime`,
    renderType: RenderType.VALUE_ONLY,
    dataSource: 'alinea',
    icon: 'alinea',
    globalOnly: true,
    chartMsg: {
      topDefault: 'bar',
      topDisableLine: true,
      countryDefault: 'line',
      countryDisableLine: true,
      statsDefault: 'line',
    },
  },
];

const alineaSteamFollowersColumn = {
  text: `dbi_metric_alinea_steam_followers`,
  value: 'alinea__steamfollowers',
  metricName: 'Alinea Steam Followers',
  dataSource: 'alinea',
  icon: 'alinea',
  info: `dbi_info_alinea__steamfollowers`,
  renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  width: 220,
  globalOnly: true,
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    countryDefault: 'line',
    countryDisableLine: true,
    statsDefault: 'line',
  },
};

/**
 * SteamSales 页面需要展示 Alinea Steam Followers，与 gamalyticSteamSalesListForSteamSalesPage 对齐
 */
export const alineaSteamSalesListForSteamSalesPage = (() => {
  const base = [...alineaSteamSalesList];
  const playtimeIndex = base.findIndex((t) => t.value === 'alinea__playtime');
  if (playtimeIndex === -1) return [...base, alineaSteamFollowersColumn];
  return [
    ...base.slice(0, playtimeIndex + 1),
    alineaSteamFollowersColumn,
    ...base.slice(playtimeIndex + 1),
  ];
})();

/**
 * SteamSales 页面专用列配置：
 * - Alinea 全部在最前（并在 Alinea playtime 后插入 Steam Followers）
 * - Gamalytic 次之（并在 Gamalytic playtime 后插入 Steam Followers）
 * - VGI 在最后（并在 VGI playtime 后插入 Total Followers）
 */
export const steamSalesListForSteamSalesPage = [
  ...alineaSteamSalesListForSteamSalesPage,
  ...gamalyticSteamSalesListForSteamSalesPage,
  ...steamSalesVgiListForSteamSalesPage,
];


export const pcMetricTableSteamSaleListPCGAME = [
  {
    text: `dbi_metric_alinea_steam_est_revenue`,
    value: 'alinea__est_revenue',
    width: 240,
    width_cn: 240,
    titleTip: true,
    info: `dbi_info_alinea_est_revenue`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_est_units`,
    value: 'alinea__est_units',
    width: 220,
    width_cn: 220,
    titleTip: true,
    info: `dbi_info_alinea_est_units`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_est_price`,
    value: 'alinea__est_price',
    width: 260,
    width_cn: 270,
    titleTip: true,
    info: `dbi_info_alinea_est_price`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_gamalytic_steam_est_revenue`,
    value: 'gamalytic__est_revenue',
    width: 360,
    width_cn: 300,
    titleTip: true,
    info: `dbi_info_gamalytic_est_revenue`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
    defaultSelect: true,
  },
  {
    // text: 'Gamalytic Steam Units',
    text: `dbi_metric_gamalytic_est_units`,
    value: 'gamalytic__est_units',
    width: 240,
    width_cn: 240,
    titleTip: true,
    info: `dbi_info_est_units`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
    defaultSelect: true,
  },
  {
    // text: 'Gamalytic Steam Price($)',
    text: `dbi_metric_gamalytic_est_price`,
    value: 'gamalytic__est_price',
    width: 260,
    width_cn: 270,
    titleTip: true,
    info: `dbi_info_est_price`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
    defaultSelect: true,
  },
  {
    // text: 'Steam Premium Revenue($)',
    text: `dbi_metric_vgi_est_revenue`,
    value: 'est_revenue',
    // width: 187,
    width: 310,
    titleTip: true,
    info: `dbi_info_est_revenue`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    // dataSourceImage: vginsights,
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
  },
  {
    // text: 'Steam Units',
    text: `dbi_metric_vgi_units`,
    value: 'est_units',
    width: 200,
    width_cn: 200,
    titleTip: true,
    info: `dbi_info_est_units`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    // dataSourceImage: vginsights,
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: true,
  },
  {
    // text: 'Steam Price($)',
    text: `dbi_metric_vgi_price`,
    value: 'est_price',
    width: 220,
    titleTip: true,
    info: `dbi_info_est_price`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    // dataSourceImage: vginsights,
    renderType: RenderType.PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: true,
  },
  // {
  //   text: 'dbi_metric_daily_wishlist',
  //   value: 'wishlists',
  //   width: 280,
  //   titleTip: true,
  //   info: `dbi_info_daily_wishlist`,
  //   dataSource: 'vginsights',
  //   dataSourceName: 'vginsights',
  //   segType: segType.engagement,
  //   dataSourceType: dataSourceType.vginsights,
  //   defaultSelect: true,
  //   newMore: true,
  // },
  // {
  //   text: `dbi_metric_lifetime_wishlist`,
  //   value: 'wishlists_total',
  //   width: 280,
  //   titleTip: true,
  //   info: 'dbi_info_lifetime_wishlist',
  //   dataSource: 'vginsights',
  //   dataSourceName: 'vginsights',
  //   segType: segType.engagement,
  //   dataSourceType: dataSourceType.vginsights,
  //   defaultSelect: true,
  //   newMore: true,
  // },
];

// export const pcMetricTableSteamSaleListPCGAME_OLD = [
//   {
//     // text: 'Steam Premium Revenue($)',
//     text: `dbi_metric_steam_est_revenue`,
//     value: 'est_revenue',
//     // width: 187,
//     width: 202,
//     width_cn: 228,
//     titleTip: true,
//     info: `dbi_info_est_revenue`,
//     dataSource: 'vginsights',
//     dataSourceName: 'vginsights',
//     // dataSourceImage: vginsights,
//     renderType: RenderType.VALUE_AND_RATIO,
//     segType: segType.revenue,
//     dataSourceType: dataSourceType.vginsights,
//   },
//   {
//     // text: 'Steam Units',
//     text: `dbi_metric_est_units`,
//     value: 'est_units',
//     width: 200,
//     titleTip: true,
//     info: `dbi_info_est_units`,
//     dataSource: 'vginsights',
//     dataSourceName: 'vginsights',
//     // dataSourceImage: vginsights,
//     renderType: RenderType.VALUE_AND_RATIO,
//     segType: segType.revenue,
//     dataSourceType: dataSourceType.vginsights,
//     defaultSelect: true,
//   },
//   {
//     // text: 'Steam Price($)',
//     text: `dbi_metric_est_price`,
//     value: 'est_price',
//     width: 280,
//     titleTip: true,
//     info: `dbi_info_est_price`,
//     dataSource: 'vginsights',
//     dataSourceName: 'vginsights',
//     // dataSourceImage: vginsights,
//     renderType: RenderType.STEAM_PRICE,
//     segType: segType.revenue,
//     dataSourceType: dataSourceType.vginsights,
//     defaultSelect: true,
//   },
//   // {
//   //   text: 'dbi_metric_daily_wishlist',
//   //   value: 'wishlists',
//   //   width: 280,
//   //   titleTip: true,
//   //   info: `dbi_info_daily_wishlist`,
//   //   dataSource: 'vginsights',
//   //   dataSourceName: 'vginsights',
//   //   segType: segType.engagement,
//   //   dataSourceType: dataSourceType.vginsights,
//   //   defaultSelect: true,
//   //   newMore: true,
//   // },
//   // {
//   //   text: `dbi_metric_lifetime_wishlist`,
//   //   value: 'wishlists_total',
//   //   width: 280,
//   //   titleTip: true,
//   //   info: 'dbi_info_lifetime_wishlist',
//   //   dataSource: 'vginsights',
//   //   dataSourceName: 'vginsights',
//   //   segType: segType.engagement,
//   //   dataSourceType: dataSourceType.vginsights,
//   //   defaultSelect: true,
//   //   newMore: true,
//   // },
// ];

// All Metrics / 下载弹窗：VGI 分组，顺序与 KPI Trends / pcSteamDataList 一致
export const pcMetricTableSteamSaleList = [
  {
    text: `dbi_metric_vgi_steam_est_revenue`,
    value: 'est_revenue',
    width_cn: 260,
    titleTip: true,
    info: `dbi_info_est_revenue`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
  },
  {
    text: `dbi_metric_vgi_steam_cumulative_revenue`,
    value: 'est_cumulative_revenue',
    titleTip: true,
    info: `dbi_info_est_revenue`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
  },
  {
    text: `dbi_metric_vgi_steam_units`,
    value: 'est_units',
    width_cn: 200,
    titleTip: true,
    info: `dbi_info_est_units`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: true,
  },
  {
    text: `dbi_metric_vgi_steam_cumulative_units`,
    value: 'est_cumulative_units',
    titleTip: true,
    info: '',
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
  },
  {
    text: `dbi_metric_vgi_steam_pcu`,
    value: 'pcu',
    titleTip: true,
    info: `dbi_info_pcu`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    icon: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.vginsights,
  },
  {
    text: `dbi_metric_vgi_steam_acu`,
    value: 'acu',
    titleTip: true,
    info: `dbi_info_acu_avg`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    icon: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.vginsights,
  },
  {
    text: 'dbi_metric_vgi_steam_daily_wishlist',
    value: 'wishlists',
    titleTip: true,
    info: `dbi_info_daily_wishlist`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    segType: segType.engagement,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: true,
    newMore: true,
  },
  {
    text: `dbi_metric_vgi_lifetime_wishlist`,
    value: 'wishlists_total',
    titleTip: true,
    info: 'dbi_info_lifetime_wishlist',
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    segType: segType.engagement,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: true,
    newMore: true,
  },
  {
    text: `dbi_metric_vgi_steam_price`,
    value: 'est_price',
    width: 220,
    titleTip: true,
    info: `dbi_info_est_price`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    renderType: RenderType.STEAM_PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.vginsights,
    defaultSelect: true,
  },
  {
    text: `dbi_metric_vgi_steam_reviews`,
    value: 'steamreviews',
    titleTip: true,
    info: `dbi_info_steamreviews`,
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    icon: 'vginsights',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.vginsights,
  },
];

// All Metrics / 下载弹窗：Gamalytic 分组，顺序与 KPI Trends / pcGamalyticDataList 一致
export const gamalyticMetricList = [
  {
    text: `dbi_metric_gamalytic_steam_est_revenue`,
    value: 'gamalytic__est_revenue',
    titleTip: true,
    info: `dbi_info_gamalytic_est_revenue`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_jptb_cumulative_revenue_trends`,
    value: 'gamalytic__est_cumulative_revenue',
    titleTip: true,
    info: `dbi_info_gamalytic_est_cumulative_revenue`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_est_units`,
    value: 'gamalytic__est_units',
    titleTip: true,
    info: `dbi_info_est_units`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_steam_cumulative_units`,
    value: 'gamalytic__est_cumulative_units',
    titleTip: true,
    info: '',
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_steam_pcu`,
    value: 'gamalytic__pcu',
    titleTip: true,
    info: `dbi_info_pcu`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    icon: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_steam_acu`,
    value: 'gamalytic__acu',
    titleTip: true,
    info: `dbi_info_acu_avg`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    icon: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_steam_followers`,
    value: 'gamalytic__steamfollowers',
    titleTip: true,
    info: `dbi_info_gamalytic__steamfollowers`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    icon: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: 'dbi_metric_gamalytic_steam_daily_wishlist',
    value: 'gamalytic__wishlists',
    titleTip: true,
    info: `dbi_info_daily_wishlist`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    segType: segType.engagement,
    dataSourceType: dataSourceType.gamalytic,
    newMore: true,
  },
  {
    text: `dbi_metric_gamalytic_steam_lifetime_wishlist`,
    value: 'gamalytic__wishlists_total',
    titleTip: true,
    info: 'dbi_info_gamalytic__lifetime_wishlist',
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    segType: segType.engagement,
    dataSourceType: dataSourceType.gamalytic,
    newMore: true,
  },
  {
    text: `dbi_metric_gamalytic_est_price`,
    value: 'gamalytic__est_price',
    titleTip: true,
    info: `dbi_info_est_price`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    renderType: RenderType.STEAM_PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: `dbi_metric_gamalytic_steam_reviews`,
    value: 'gamalytic__steamreviews',
    titleTip: true,
    info: `dbi_info_steamreviews`,
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    icon: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.gamalytic,
  },
  {
    text: 'dbi_metric_gamalytic_steam_total_reviews',
    value: 'gamalytic__cumulative_reviews',
    titleTip: true,
    info: 'dbi_info_gamalytic__cumulative_reviews',
    dataSource: 'gamalytic',
    dataSourceName: 'gamalytic',
    icon: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.gamalytic,
  },
];

// All Metrics / 下载弹窗：Alinea Analytics 分组，顺序与 KPI Trends / 榜单 Steam Data 下 Alinea 一致
export const alineaMetricList = [
  {
    text: `dbi_metric_alinea_steam_est_revenue`,
    value: 'alinea__est_revenue',
    titleTip: true,
    info: `dbi_info_alinea_est_revenue`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_jptb_cumulative_revenue_trends`,
    value: 'alinea__est_cumulative_revenue',
    titleTip: true,
    info: `dbi_info_alinea_est_cumulative_revenue`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_est_units`,
    value: 'alinea__est_units',
    titleTip: true,
    info: `dbi_info_alinea_est_units`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
    defaultSelect: true,
  },
  {
    text: `dbi_metric_alinea_steam_cumulative_units`,
    value: 'alinea__est_cumulative_units',
    titleTip: true,
    info: '',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_pcu',
    value: 'alinea__pcu',
    titleTip: true,
    info: `dbi_info_alinea_pcu`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_acu',
    value: 'alinea__acu',
    titleTip: true,
    info: `dbi_info_alinea_acu`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_steam_followers`,
    value: 'alinea__steamfollowers',
    titleTip: true,
    info: `dbi_info_alinea__steamfollowers`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_daily_wishlist',
    value: 'alinea__wishlists',
    titleTip: true,
    info: `dbi_info_daily_wishlist`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
    defaultSelect: true,
    newMore: true,
  },
  {
    text: `dbi_metric_alinea_steam_lifetime_wishlist`,
    value: 'alinea__wishlists_total',
    titleTip: true,
    info: 'dbi_info_alinea__lifetime_wishlist',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
    defaultSelect: true,
    newMore: true,
  },
  {
    text: `dbi_metric_alinea_est_price`,
    value: 'alinea__est_price',
    titleTip: true,
    info: `dbi_info_alinea_est_price`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.STEAM_PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
    defaultSelect: true,
  },
  {
    text: `dbi_metric_alinea_lifetime_weighted_price_trends`,
    value: 'alinea__lifetime_weighted_price',
    titleTip: true,
    info: '',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_playtime_steam`,
    value: 'alinea__playtime',
    titleTip: true,
    info: 'dbi_info_alinea__playtime',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_players',
    value: 'alinea__players',
    titleTip: true,
    info: 'dbi_info_alinea__players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_total_players',
    value: 'alinea__players_total',
    titleTip: true,
    info: 'dbi_info_alinea__players_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_steam_reviews`,
    value: 'alinea__steamreviews',
    titleTip: true,
    info: `dbi_info_alinea__steamreviews`,
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_total_reviews',
    value: 'alinea__cumulative_reviews',
    titleTip: true,
    info: 'dbi_info_alinea__cumulative_reviews',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_positive_reviews',
    value: 'alinea__positive',
    titleTip: true,
    info: 'dbi_info_alinea__positive',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_steam_total_positive_reviews',
    value: 'alinea__cumulative_positive',
    titleTip: true,
    info: 'dbi_info_alinea__cumulative_positive',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: `dbi_metric_alinea_dau`,
    value: 'alinea__dau',
    titleTip: true,
    info: 'dbi_info_alinea__dau',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
    dateEnum: ['daily'],
  },
  {
    text: `dbi_metric_alinea_mau`,
    value: 'alinea__mau',
    titleTip: true,
    info: 'dbi_info_alinea__mau',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
    dateEnum: ['monthly'],
  },
  {
    text: 'dbi_metric_alinea_ps_revenue',
    value: 'alinea__ps_est_revenue',
    titleTip: true,
    info: 'dbi_info_alinea_ps_revenue',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_revenue_total',
    value: 'alinea__ps_est_cumulative_revenue',
    titleTip: true,
    info: 'dbi_info_alinea_ps_revenue_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_units',
    value: 'alinea__ps_est_units',
    titleTip: true,
    info: 'dbi_info_alinea_ps_units',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_units_total',
    value: 'alinea__ps_est_cumulative_units',
    titleTip: true,
    info: 'dbi_info_alinea_ps_units_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_lifetime_players',
    value: 'alinea__ps_players',
    titleTip: true,
    info: 'dbi_info_alinea_ps_lifetime_players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_plus_total_players',
    value: 'alinea__ps_players_total',
    titleTip: true,
    info: 'dbi_info_alinea_ps_plus_total_players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_game_pass_players',
    value: 'alinea__ps_game_pass_players',
    titleTip: true,
    info: 'dbi_info_alinea_ps_game_pass_players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_game_pass_players_total',
    value: 'alinea__ps_game_pass_players_total',
    titleTip: true,
    info: 'dbi_info_alinea_ps_game_pass_players_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_ps_dau',
    value: 'alinea__ps_dau',
    titleTip: true,
    info: 'dbi_info_alinea_ps_dau',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
    dateEnum: ['daily'],
  },
  {
    text: 'dbi_metric_alinea_ps_mau',
    value: 'alinea__ps_mau',
    titleTip: true,
    info: 'dbi_info_alinea_ps_mau',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
    dateEnum: ['monthly'],
  },
  {
    text: 'dbi_metric_alinea_ps_price',
    value: 'alinea__ps_price',
    titleTip: true,
    info: 'dbi_info_alinea_ps_price',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.STEAM_PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_revenue',
    value: 'alinea__xbox_est_revenue',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_revenue',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_revenue_total',
    value: 'alinea__xbox_est_cumulative_revenue',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_revenue_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_units',
    value: 'alinea__xbox_est_units',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_units',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_units_total',
    value: 'alinea__xbox_est_cumulative_units',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_units_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_players',
    value: 'alinea__xbox_players',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_game_pass_total_players',
    value: 'alinea__xbox_players_total',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_game_pass_total_players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_game_pass_players',
    value: 'alinea__xbox_game_pass_players',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_game_pass_players',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_game_pass_players_total',
    value: 'alinea__xbox_game_pass_players_total',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_game_pass_players_total',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.engagement,
    dataSourceType: dataSourceType.alinea,
  },
  {
    text: 'dbi_metric_alinea_xbox_dau',
    value: 'alinea__xbox_dau',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_dau',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
    dateEnum: ['daily'],
  },
  {
    text: 'dbi_metric_alinea_xbox_mau',
    value: 'alinea__xbox_mau',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_mau',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.alinea,
    dateEnum: ['monthly'],
  },
  {
    text: 'dbi_metric_alinea_xbox_price',
    value: 'alinea__xbox_price',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_price',
    dataSource: 'alinea',
    dataSourceName: 'alinea',
    icon: 'alinea',
    renderType: RenderType.STEAM_PRICE,
    segType: segType.revenue,
    dataSourceType: dataSourceType.alinea,
  },
];

const pcConsoleDetailTrendChartMsg = {
  topDefault: 'line',
  countryDefault: 'line',
  statsDefault: 'line',
};

const pcConsoleAlineaActiveUserDetails = alineaMetricList.filter((item) =>
  ['alinea__dau', 'alinea__mau'].includes(item?.value),
);

const pcConsoleAlineaEngagementDetails = [
  {
    text: 'dbi_metric_alinea_steam_players',
    value: 'alinea__players',
    info: 'dbi_info_alinea__players',
    dataSource: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    globalOnly: true,
    chartMsg: pcConsoleDetailTrendChartMsg,
  },
  {
    text: 'dbi_metric_alinea_steam_total_players',
    value: 'alinea__players_total',
    info: 'dbi_info_alinea__players_total',
    dataSource: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    globalOnly: true,
    chartMsg: pcConsoleDetailTrendChartMsg,
  },
];

const pcConsoleAlineaReviewDetails = [
  alineaSteamReviewsDetail,
  {
    text: 'dbi_metric_alinea_steam_total_reviews',
    value: 'alinea__cumulative_reviews',
    info: 'dbi_info_alinea__cumulative_reviews',
    dataSource: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    globalOnly: true,
    chartMsg: pcConsoleDetailTrendChartMsg,
  },
  {
    text: 'dbi_metric_alinea_steam_positive_reviews',
    value: 'alinea__positive',
    info: 'dbi_info_alinea__positive',
    dataSource: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    globalOnly: true,
    chartMsg: pcConsoleDetailTrendChartMsg,
  },
  {
    text: 'dbi_metric_alinea_steam_total_positive_reviews',
    value: 'alinea__cumulative_positive',
    info: 'dbi_info_alinea__cumulative_positive',
    dataSource: 'alinea',
    icon: 'alinea',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    globalOnly: true,
    chartMsg: pcConsoleDetailTrendChartMsg,
  },
];

const pcConsoleGamalyticReviewDetails = [
  gamalyticSteamReviewsDetail,
  {
    text: 'dbi_metric_gamalytic_steam_total_reviews',
    value: 'gamalytic__cumulative_reviews',
    info: 'dbi_info_gamalytic__cumulative_reviews',
    dataSource: 'gamalytic',
    icon: 'gamalytic',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 300,
    globalOnly: true,
    chartMsg: pcConsoleDetailTrendChartMsg,
  },
];

const pcConsoleAlineaWishlistsByCountryDetail = {
  text: 'dbi_metric_alinea_wishlists_by_country',
  metricName: 'Wishlists by countries',
  value: 'alinea__wishlists_by_country',
  info: 'dbi_info_alinea__wishlists_by_country',
  renderType: RenderType.COUNTRY_CHART,
  dataSource: 'alinea',
  icon: 'alinea',
  width: 360,
  chartMsg: {
    topDefault: 'bar',
    topDisableLine: true,
    topDisablePie: true,
    countryDefault: 'bar',
    countryDisableLine: true,
    countryDisablePie: true,
    statsDefault: 'bar',
  },
};

/**
 * PC&Console Stats 使用独立的最终指标集合，避免把新增指标扩散到共用同一详情配置的 PC Games。
 * 数组顺序就是详情页同数据源下的产品顺序，不再通过位置标记或 value 规则二次插入。
 */
export const pcConsoleAlineaStatsDetailList = [
  ...alineaSalesBaseDetailList,
  ...alineaPCUList,
  ...pcConsoleAlineaActiveUserDetails,
  alineaPlaytimeDetail,
  ...pcConsoleAlineaEngagementDetails,
  alineaPlayersByCountryDetail,
  alineaSteamFollowersDetail,
  ...pcConsoleAlineaReviewDetails,
];

export const pcConsoleAlineaCountriesStatsDetailList = [
  ...alineaSalesBaseDetailList,
  ...alineaPCUList,
  ...pcConsoleAlineaActiveUserDetails,
  alineaPlaytimeDetail,
  ...pcConsoleAlineaEngagementDetails,
  alineaPlayersByCountryDetail,
  pcConsoleAlineaWishlistsByCountryDetail,
  ...pcConsoleAlineaReviewDetails,
];

export const pcConsoleGamalyticStatsDetailList = [
  ...gamalyticPCUList,
  gamalyticSteamFollowersDetail,
  ...gamalyticSalesDetailList,
  ...pcConsoleGamalyticReviewDetails,
];

export const pcConsoleGamalyticCountriesStatsDetailList = [
  ...gamalyticPCUList,
  ...gamalyticSalesDetailList,
  ...pcConsoleGamalyticReviewDetails,
];

export const pcMetricTableCloums = [
  ...metricsTablePCUFollwerList.map((item) =>
    item.value === 'steamfollowers'
      ? { ...item, hideBubbleLeadingIcon: true }
      : item,
  ),
  ...digitalRevenueList,
  ...gsdSegmentList,
  ...pcMetricTableSteamSaleListPCGAME,
  ...npdList,
  ...newzooList,
];

// export const pcMetricTableCloumsOld = [
//   // ...metricsTablePCUFollwerListOld,
//   ...metricsTablePCUFollwerList,
//   ...digitalRevenueList,
//   ...gsdSegmentList,
//   ...pcMetricTableSteamSaleListPCGAME_OLD,
//   ...npdList,
//   ...newzooList,
// ];

export const consleMetricTableCloums = [...digitalRevenueList, ...gsdSegmentList, ...npdList, ...newzooList];

export const consleMetricTableSegementList = [
  {
    // text: 'Revenue',
    text: `dbi_revenue`,
    value: 'Revenue',
    stepValue: digitalRevenueList[0].value,
  },
  {
    // text: 'MAU',
    text: `dbi_mau`,
    value: 'MAU',
    stepValue: newzooList[0].value,
  },
  // {
  //   // text: 'Engagement',
  //   value: 'Engagement',
  //   text: `dbi_metric_engagement`,
  //   stepValue: 'avg_daily_playtime',
  // },
  {
    // text: 'Engagement',
    value: 'Engagement',
    text: `dbi_metric_engagement_active_level`,
    stepValue: 'avg_daily_playtime',
  },
  {
    // text: 'Churn %/Acquisition %',
    text: `dbi_metric_churn_acquisition`,
    value: 'ChurnAcquisition',
    stepValue: 'churn',
  },
  {
    // text: 'Time Spent',
    text: `dbi_metric_atime_spent_0H`,
    value: 'TimeSpent',
    stepValue: 'time_spent_0H',
  },
  {
    // text: 'Retention',
    text: `dbi_metric_retention`,
    value: 'Retention',
    stepValue: 'retention_d1',
  },
];

export const pcMetricTableSegementList = [
  {
    text: 'Steam',
    value: 'Steam',
    // value: metricsTablePCUFollwerList[0].value,
    stepValue: metricsTablePCUFollwerList[3].value,
    distanceOffset: -360, // 减去固定的游戏名列宽度
  },
  ...consleMetricTableSegementList,
];

export const japanSalesSegOptions: any = [
  {
    // text: 'Sales of selected time',
    text: `dbi_metric_jpseg_sales`,
    value: 'sales',
  },
  {
    // text: 'Revenue of selected time',
    text: `dbi_metric_jpseg_revenue`,
    value: 'revenue',
  },
  {
    // text: 'Cumulative Sales',
    text: `dbi_metric_jpseg_cumulative_sales`,
    value: 'cumulative_sales',
  },
  {
    // text: 'Cumulative Revenue',
    text: `dbi_metric_jpseg_cumulative_revenue`,
    value: 'cumulative_revenue',
  },
  {
    // text: 'Price',
    text: `dbi_metric_jpseg_price`,
    value: 'price',
  },
];

export const mobileDetailsOverlapSegOptions: any[] = [
  {
    text: 'dbi_metric_mobile_cross_usage',
    value: SENSORTOWER_METRIC_KEY_MAP.CROSS_APP_USAGE,
  },
  {
    text: 'dbi_metric_mobile_cross_affinity',
    value: SENSORTOWER_METRIC_KEY_MAP.CROSS_AFFINITY,
  },
];

// Famitsu/Japan Sales metric definitions — kept for rollback.
export const japanSalesList: any = [
  {
    // text: 'Sales of selected time', //表格头部
    text: `dbi_metric_jptb_sales`,
    // metricName: 'Sales of selected time', //卡片指标名及SortBy展示
    value: 'sales', // 参数值
    // width: 228, // 表头宽度； 详情页和榜单共用，请注意榜单有ICON情况
    // width_cn: 250,
    // width: 220, //表头宽度； 详情页和榜单共用，请注意榜单有ICON情况
    // width_cn: 190,
    dataSource: 'famitsu',
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.famitsu,
    tabName: 'gsd_digitalRevenue',
  },
  {
    // text: 'Price($)',
    text: `dbi_metric_famitsu_price`,
    // metricName: 'Price',
    value: 'price',
    // width: 220,
    // width_cn: 240,
    // width: 220,
    // width_cn: 170,
    dataSource: 'famitsu',
    textContent: `dbi_top_console_japan_sales_price_columns_tips`,
    sumavgArr: [],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.famitsu,
    tabName: 'gsd_digitalRevenue',
  },
  {
    // text: 'Revenue of selected time($)',
    text: `dbi_metric_jptb_revenue`,
    // metricName: 'Revenue of selected time',
    value: 'revenue',
    dataSource: 'famitsu',
    // width: 290,
    // width_cn: 270,
    // width: 213,
    // width_cn: 213,
    sumavgArr: [SUM, AVG],
    hasByDevice: true,
    segType: segType.revenue,
    dataSourceType: dataSourceType.famitsu,
    tabName: 'gsd_digitalRevenue',
  },
  {
    // text: 'Cumulative Sales of the latest week',
    text: `dbi_metric_jptb_cumulative_sales`,
    // metricName: 'Cumulative Sales',
    value: 'cumulative_sales',
    dataSource: 'famitsu',
    // width: 233,
    // width_cn: 240,
    // width: 240,
    // width_cn: 180,
    sumavgArr: [],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.famitsu,
    tabName: 'gsd_digitalRevenue',
  },
  {
    // text: 'Cumulative Revenue of the latest week($)',
    text: `dbi_metric_jptb_cumulative_revenue`,
    // metricName: 'Cumulative Revenue',
    value: 'cumulative_revenue',
    dataSource: 'famitsu',
    // width: 252,
    // width_cn: 199,
    sumavgArr: [],
    hasByDevice: false,
    segType: segType.revenue,
    dataSourceType: dataSourceType.famitsu,
    tabName: 'gsd_digitalRevenue',
  },
];

// Famitsu/Japan Sales metric definitions — kept for rollback.
export const consoleJapanSalesList = [
  {
    text: `dbi_top_sales_of_selected_time`,
    value: 'famitsu_sales',
    dataSource: 'famitsu',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO,
    // width: 240,
    // width_cn: 240,
  },
  {
    text: `dbi_top_price$`,
    value: 'famitsu_price',
    dataSource: 'famitsu',
    info: 'dbi_top_console_japan_sales_price_columns_tips',
    renderType: RenderType.VALUE_AND_RATIO,
    // width: 180,
    // width_cn: 180,
  },
  {
    text: `dbi_top_revenue_of_selected_time$`,
    value: 'famitsu_revenue',
    dataSource: 'famitsu',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO,
    // width: 300,
    // width_cn: 300,
  },
  {
    text: `dbi_top_cumulative_sales_of_the_latest_week`,
    value: 'famitsu_cumulative_sales',
    dataSource: 'famitsu',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO,
    // width: 330,
    // width_cn: 330,
  },
  {
    text: `dbi_top_cumulative_revenue_of_the_latest_week$`,
    value: 'famitsu_cumulative_revenue',
    dataSource: 'famitsu',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO,
    // width: 374,
    // width_cn: 374,
  },
];

export const pcSteamDataList = [
  {
    text: 'dbi_metric_source_steam_pcu',
    value: 'source_steam__pcu',
    dataSource: 'source_steam',
    icon: 'source_steam',
    metricValue: 'source_steam',
    info: `dbi_info_source_steam_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.source_steam,
    tabName: 'pcu',
  },
  {
    text: 'dbi_metric_source_steam_acu',
    value: 'source_steam__acu',
    dataSource: 'source_steam',
    icon: 'source_steam',
    metricValue: 'source_steam',
    info: `dbi_info_source_steam_acu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    sumavgArr: [MAX, AVG],
    hasByDevice: false,
    segType: segType.activeuser,
    dataSourceType: dataSourceType.source_steam,
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_vgi_steam_followers`,
    value: 'steamfollowers',
    dataSource: 'steam',
    icon: 'steam',
    metricValue: 'steamcharts',
    info: `dbi_info_steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSourceType: 'steamcharts',
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_vgi_steam_est_revenue`,
    value: 'est_revenue',
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    titleTip: true,
    info: `dbi_info_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 320,
    // width_cn: 320,
  },
  {
    text: `dbi_metric_vgi_steam_cumulative_revenue`,
    value: 'est_cumulative_revenue',
    dataSource: 'vginsights',
    info: `dbi_info_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 360,
    // width_cn: 360,
  },
  {
    text: `dbi_metric_vgi_steam_units`,
    value: 'est_units',
    dataSource: 'vginsights',
    info: `dbi_info_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 300,
    // width_cn: 300,
    chartMsg: {
      topDefault: 'bar',
      countryDefault: 'bar',
      statsDefault: 'bar',
    },
  },
  {
    text: `dbi_metric_vgi_steam_cumulative_units`,
    value: 'est_cumulative_units',
    dataSource: 'vginsights',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 300,
    // width_cn: 300,
  },
  {
    text: `dbi_metric_vgi_steam_pcu`,
    value: 'pcu',
    dataSource: 'steamcharts',
    icon: 'vginsights',
    metricValue: 'steamcharts',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_vgi_steam_acu`,
    value: 'acu',
    dataSource: 'steamcharts',
    metricValue: 'steamcharts',
    icon: 'vginsights',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'steamcharts',
    tabName: 'pcu',
  },
  // 放到 steam data
  {
    text: 'dbi_metric_vgi_steam_daily_wishlist',
    value: 'wishlists',
    info: 'dbi_info_daily_wishlist',
    infoDetail: 'dbi_info_daily_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 300,
    // width_cn: 300,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_vgi_steam_lifetime_wishlist`,
    value: 'wishlists_total',
    info: 'dbi_info_lifetime_wishlist',
    infoDetail: `dbi_info_lifetime_wishlist`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    dataSource: 'vginsights',
    // width: 300,
    // width_cn: 300,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_vgi_steam_price`,
    value: 'est_price',
    dataSource: 'vginsights',
    dataSourceName: 'vginsights',
    titleTip: true,
    info: `dbi_info_est_price`,
    renderType: RenderType.STEAM_PRICE,
    // width: 300,
    // width_cn: 300,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_vgi_steam_reviews`,
    value: 'steamreviews',
    dataSource: 'vginsights',
    icon: 'vginsights',
    metricValue: 'vginsights',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 300,
    // width_cn: 300,
    tabName: 'pcu',
  },
  // PC&Console 移除 VGI Steam Weighted Price
  // {
  //   text: `dbi_metric_steam_weighted_price$`,
  //   value: 'weighted_price',
  //   dataSource: 'vginsights',
  //   info: `dbi_info_weighted_price`,
  //   renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
  //   width: 230,
  //   width_cn: 230,
  // },
];

// Metrics selector: Gamalytic group (order aligned with KPI Trends MetricsSelection/config.ts)
export const pcGamalyticDataList = [
  {
    text: `dbi_metric_gamalytic_steam_est_revenue`,
    value: 'gamalytic__est_revenue',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    titleTip: true,
    info: `dbi_info_gamalytic_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 360,
    // width_cn: 360,
  },
  {
    text: `dbi_metric_gamalytic_jptb_cumulative_revenue_trends`,
    value: 'gamalytic__est_cumulative_revenue',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    info: `dbi_info_gamalytic_est_cumulative_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 380,
    // width_cn: 380,
  },
  {
    text: `dbi_metric_gamalytic_est_units`,
    value: 'gamalytic__est_units',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    info: `dbi_info_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 300,
    // width_cn: 300,
  },
  {
    text: `dbi_metric_gamalytic_steam_cumulative_units`,
    value: 'gamalytic__est_cumulative_units',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 360,
    // width_cn: 360,
  },
  {
    text: `dbi_metric_gamalytic_steam_pcu`,
    value: 'gamalytic__pcu',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_gamalytic_steam_acu`,
    value: 'gamalytic__acu',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_acu_avg`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'gamalytic',
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_gamalytic_steam_followers`,
    value: 'gamalytic__steamfollowers',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_gamalytic__steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 300,
    // width_cn: 300,
    dataSourceType: 'gamalytic',
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_gamalytic_steam_daily_wishlist`,
    value: 'gamalytic__wishlists',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    info: 'dbi_info_daily_wishlist',
    infoDetail: 'dbi_info_daily_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 340,
    // width_cn: 340,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_gamalytic_steam_lifetime_wishlist`,
    value: 'gamalytic__wishlists_total',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    info: 'dbi_info_gamalytic__lifetime_wishlist',
    infoDetail: `dbi_info_gamalytic__lifetime_wishlist`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 360,
    // width_cn: 360,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_gamalytic_est_price`,
    value: 'gamalytic__est_price',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    titleTip: true,
    info: `dbi_info_est_price`,
    renderType: RenderType.STEAM_PRICE,
    // width: 300,
    // width_cn: 300,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_gamalytic_steam_reviews`,
    value: 'gamalytic__steamreviews',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    icon: 'gamalytic',
    info: `dbi_info_steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    // width: 300,
    // width_cn: 300,
    tabName: 'pcu',
  },
  {
    text: 'dbi_metric_gamalytic_steam_total_reviews',
    value: 'gamalytic__cumulative_reviews',
    dataSource: 'gamalytic',
    metricValue: 'gamalytic',
    icon: 'gamalytic',
    info: 'dbi_info_gamalytic__cumulative_reviews',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 300,
    tabName: 'pcu',
  },
];

// Metrics selector: Alinea group (order aligned with KPI Trends MetricsSelection/config.ts)
// - 用于 PC/Console MetricsTable 的指标筛选器，展示在 Steam Data 下、Gamalytic 前
// - value 保持 alinea__ 前缀，确保后端请求仍能把 alinea 指标归到 alinea 数组中
export const pcAlineaDataList = [
  {
    text: `dbi_metric_alinea_steam_est_revenue`,
    value: 'alinea__est_revenue',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: `dbi_info_alinea_est_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 240,
    width_cn: 240,
  },
  {
    text: `dbi_metric_alinea_jptb_cumulative_revenue_trends`,
    value: 'alinea__est_cumulative_revenue',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_est_cumulative_revenue`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 240,
    width_cn: 240,
  },
  {
    text: `dbi_metric_alinea_est_units`,
    value: 'alinea__est_units',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_est_units`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    width_cn: 220,
  },
  {
    text: `dbi_metric_alinea_steam_cumulative_units`,
    value: 'alinea__est_cumulative_units',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: '',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 240,
    width_cn: 240,
  },
  {
    text: `dbi_metric_alinea_steam_pcu`,
    value: 'alinea__pcu',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_pcu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_alinea_steam_acu`,
    value: 'alinea__acu',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea_acu`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'alinea',
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_alinea_steam_followers`,
    value: 'alinea__steamfollowers',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea__steamfollowers`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'alinea',
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_alinea_steam_daily_wishlist`,
    value: 'alinea__wishlists',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_daily_wishlist',
    infoDetail: 'dbi_info_daily_wishlist',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_alinea_steam_lifetime_wishlist`,
    value: 'alinea__wishlists_total',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__lifetime_wishlist',
    infoDetail: `dbi_info_alinea__lifetime_wishlist`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    sumavgArr: [],
    isNoMore: true,
    newMore: true,
  },
  {
    text: `dbi_metric_alinea_est_price`,
    value: 'alinea__est_price',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: `dbi_info_alinea_est_price`,
    renderType: RenderType.STEAM_PRICE,
    globalOnly: true,
    chartMsg: {
      topDefault: 'line',
      countryDefault: 'line',
      statsDefault: 'line',
    },
  },
  {
    text: `dbi_metric_alinea_playtime_steam`,
    value: 'alinea__playtime',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__playtime',
    renderType: RenderType.VALUE_ONLY,
    width: 220,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_steam_players',
    value: 'alinea__players',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
  },
  {
    text: 'dbi_metric_alinea_steam_total_players',
    value: 'alinea__players_total',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__players_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
  },
  {
    text: `dbi_metric_alinea_steam_reviews`,
    value: 'alinea__steamreviews',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: `dbi_info_alinea__steamreviews`,
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'alinea',
    tabName: 'pcu',
  },
  {
    text: 'dbi_metric_alinea_steam_total_reviews',
    value: 'alinea__cumulative_reviews',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__cumulative_reviews',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'alinea',
    tabName: 'pcu',
  },
  {
    text: 'dbi_metric_alinea_steam_positive_reviews',
    value: 'alinea__positive',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__positive',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'alinea',
    tabName: 'pcu',
  },
  {
    text: 'dbi_metric_alinea_steam_total_positive_reviews',
    value: 'alinea__cumulative_positive',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__cumulative_positive',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    dataSourceType: 'alinea',
    tabName: 'pcu',
  },
  {
    text: `dbi_metric_alinea_dau`,
    value: 'alinea__dau',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__dau',
    infoDetail: 'dbi_info_alinea__dau',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    sumavgArr: [],
    isNoMore: false,
    dateEnum: ['daily'],
  },
  {
    text: `dbi_metric_alinea_mau`,
    value: 'alinea__mau',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    info: 'dbi_info_alinea__mau',
    infoDetail: 'dbi_info_alinea__mau',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    sumavgArr: [],
    isNoMore: false,
    dateEnum: ['monthly'],
  },
];

// Metrics selector: Console Data group (Alinea PS / Xbox)
export const pcConsoleDataList = [
  {
    text: 'dbi_metric_alinea_ps_revenue',
    value: 'alinea__ps_est_revenue',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_revenue',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 160,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_revenue_total',
    value: 'alinea__ps_est_cumulative_revenue',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_revenue_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    width_cn: 180,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_units',
    value: 'alinea__ps_est_units',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_units',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 180,
    width_cn: 140,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_units_total',
    value: 'alinea__ps_est_cumulative_units',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_units_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 160,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_lifetime_players',
    value: 'alinea__ps_players',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_lifetime_players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    width_cn: 200,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_plus_total_players',
    value: 'alinea__ps_players_total',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_plus_total_players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 240,
    width_cn: 220,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_game_pass_players',
    value: 'alinea__ps_game_pass_players',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_game_pass_players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 280,
    width_cn: 260,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_game_pass_players_total',
    value: 'alinea__ps_game_pass_players_total',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_game_pass_players_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 300,
    width_cn: 280,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_ps_dau',
    value: 'alinea__ps_dau',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_dau',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 180,
    globalOnly: true,
    dateEnum: ['daily'],
  },
  {
    text: 'dbi_metric_alinea_ps_mau',
    value: 'alinea__ps_mau',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_mau',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 180,
    globalOnly: true,
    dateEnum: ['monthly'],
  },
  {
    text: 'dbi_metric_alinea_ps_price',
    value: 'alinea__ps_price',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_ps_price',
    renderType: RenderType.STEAM_PRICE,
    width: 180,
    width_cn: 140,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_revenue',
    value: 'alinea__xbox_est_revenue',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_revenue',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 160,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_revenue_total',
    value: 'alinea__xbox_est_cumulative_revenue',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_revenue_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 220,
    width_cn: 180,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_units',
    value: 'alinea__xbox_est_units',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_units',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 180,
    width_cn: 140,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_units_total',
    value: 'alinea__xbox_est_cumulative_units',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_units_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 160,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_players',
    value: 'alinea__xbox_players',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 160,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_game_pass_total_players',
    value: 'alinea__xbox_players_total',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_game_pass_total_players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 280,
    width_cn: 260,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_game_pass_players',
    value: 'alinea__xbox_game_pass_players',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_game_pass_players',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 280,
    width_cn: 260,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_game_pass_players_total',
    value: 'alinea__xbox_game_pass_players_total',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_game_pass_players_total',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 300,
    width_cn: 280,
    globalOnly: true,
  },
  {
    text: 'dbi_metric_alinea_xbox_dau',
    value: 'alinea__xbox_dau',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_dau',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 180,
    globalOnly: true,
    dateEnum: ['daily'],
  },
  {
    text: 'dbi_metric_alinea_xbox_mau',
    value: 'alinea__xbox_mau',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_mau',
    renderType: RenderType.VALUE_AND_RATIO_NOPOINT,
    width: 200,
    width_cn: 180,
    globalOnly: true,
    dateEnum: ['monthly'],
  },
  {
    text: 'dbi_metric_alinea_xbox_price',
    value: 'alinea__xbox_price',
    dataSource: 'alinea',
    metricValue: 'alinea',
    icon: 'alinea',
    titleTip: true,
    info: 'dbi_info_alinea_xbox_price',
    renderType: RenderType.STEAM_PRICE,
    width: 180,
    width_cn: 140,
    globalOnly: true,
  },
];

const pcConsoleMetricsDisabledTipsLocaleKeysMap: {
  [key: string]: string;
} = {
  revenue: 'dbi_info_digital_revenue_metric_disabled_tips',
  units: 'dbi_info_units_metric_disabled_tips',
  ast: 'dbi_info_ast_metric_disabled_tips',
  digitalARPPU: 'dbi_info_digital_arppu_metric_disabled_tips',
  alinea__mau: 'dbi_info_alinea_mau_metric_disabled_tips',
  alinea__dau: 'dbi_info_alinea_dau_metric_disabled_tips',
  alinea__ps_dau: 'dbi_info_alinea_dau_metric_disabled_tips',
  alinea__xbox_dau: 'dbi_info_alinea_dau_metric_disabled_tips',
  alinea__ps_mau: 'dbi_info_alinea_mau_metric_disabled_tips',
  alinea__xbox_mau: 'dbi_info_alinea_mau_metric_disabled_tips',
};

const pcAlineaCategoryFields = {
  category: 'alinea',
  categoryNameEN: 'Alinea',
  categoryNameCN: 'Alinea',
  categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
};


/**
 * 详情页指标在原始定义处声明 displayCategory 后，由这里统一收集。
 * 未声明分类的详情指标不进入 PC&Console 榜单选择器，避免把详情专用指标整体带入。
 */
const pcConsoleDetailCategoryMetrics = [
  ...alineaSalesDetailList,
  ...gamalyticSalesDetailList,
  ...steamSalesDetailList,
].filter((item) => item?.displayCategory);

export const pcConsoleCategoryMetrics = [
  ...appendDynamicFields2ListData(digitalRevenueListNew, (payload: any) => {
    const metricDisabledTipsLocaleKey = pcConsoleMetricsDisabledTipsLocaleKeysMap[payload?.value];
    return {
      category: 'revenue',
      displayCategory: 'sales',
      categoryNameEN: 'Revenue',
      categoryNameCN: '收入',
      categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
      metricDisabledTipsLocaleKey,
    };
  }),
  ...appendFields2ListData(npdList?.slice(0, 1), {
    category: 'revenue',
    displayCategory: 'sales',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_physical_revenue_metric_disabled_tips',
  }),
  ...appendFields2ListData(npdList?.slice(1, 2), {
    category: 'revenue',
    displayCategory: 'sales',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_revenue_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_physical_units_metric_disabled_tips',
  }),
  ...appendFields2ListData(gsdSegmentList, {
    category: 'revenue',
    displayCategory: 'sales',
    categoryNameEN: 'Revenue',
    categoryNameCN: '收入',
    categoryDisabledTipsLocaleKey: 'dbi_info_gsd_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_gsd_metrics_category_disabled_tips',
  }),
  // Insert Alinea above Gamalytic in metrics selector
  ...appendDynamicFields2ListData(
    pcAlineaDataList,
    (payload: any) => {
      const metricDisabledTipsLocaleKey =
        pcConsoleMetricsDisabledTipsLocaleKeysMap[payload?.value] ||
        'dbi_info_steam_data_metrics_category_disabled_tips';
      const displayCategory = getPCConsoleDisplayCategory('alinea', payload?.value);
      return {
        ...pcAlineaCategoryFields,
        displayCategory,
        ...PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[displayCategory],
        metricDisabledTipsLocaleKey,
      };
    },
  ),
  // Insert Gamalytic above Steam Data in metrics selector
  ...appendDynamicFields2ListData(pcGamalyticDataList, (payload: any) => ({
    category: 'gamalytic',
    categoryNameEN: 'Gamalytic',
    categoryNameCN: 'Gamalytic',
    categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
    displayCategory: getPCConsoleDisplayCategory('gamalytic', payload?.value),
    ...PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[
      getPCConsoleDisplayCategory('gamalytic', payload?.value)
    ],
  })),
  ...appendDynamicFields2ListData(pcSteamDataList, (payload: any) => ({
    category: 'steamData',
    categoryNameEN: 'Steam Data',
    categoryNameCN: 'Steam数据',
    categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
    displayCategory: getPCConsoleDisplayCategory('steamData', payload?.value),
    ...PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[
      getPCConsoleDisplayCategory('steamData', payload?.value)
    ],
  })),
  ...appendDynamicFields2ListData(pcConsoleDetailCategoryMetrics, (payload: any) => {
    const category = payload?.dataSource === 'vginsights' ? 'steamData' : payload?.dataSource;
    const displayCategory = payload?.displayCategory;
    return {
      category,
      displayCategory,
      ...PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[displayCategory],
      metricDisabledTipsLocaleKey:
        PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[displayCategory]?.categoryDisabledTipsLocaleKey,
    };
  }),
  ...appendDynamicFields2ListData(
    pcConsoleDataList.filter(
      (item) => !['alinea__ps_price', 'alinea__xbox_price'].includes(item?.value),
    ),
    (payload: any) => {
      const metricDisabledTipsLocaleKey =
        pcConsoleMetricsDisabledTipsLocaleKeysMap[payload?.value] ||
        'dbi_info_steam_data_metrics_category_disabled_tips';
      const displayCategory = getPCConsoleDisplayCategory('consoleData', payload?.value);
      return {
        category: 'consoleData',
        categoryNameEN: 'Console Data',
        categoryNameCN: '主机数据',
        categoryDisabledTipsLocaleKey: 'dbi_info_steam_data_metrics_category_disabled_tips',
        displayCategory,
        ...PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[displayCategory],
        metricDisabledTipsLocaleKey,
      };
    },
  ),
  // ...appendFields2ListData(consoleJapanSalesList, {
  //   category: 'japanSales',
  //   categoryNameEN: 'Japan Sales',
  //   categoryNameCN: '日本销售额',
  //   categoryDisabledTipsLocaleKey: 'dbi_info_console_japan_sales_metrics_category_disabled_tips',
  //   metricDisabledTipsLocaleKey: 'dbi_info_console_japan_sales_metrics_category_disabled_tips',
  // }),
  ...appendFields2ListData(ampereMAUList, {
    category: 'mau',
    categoryNameEN: 'MAU',
    categoryNameCN: '月活跃玩家数',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_mau_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_ampere_mau_category_disabled_tips',
  }),
  ...appendFields2ListData(ampereDAUList, {
    category: 'dau',
    categoryNameEN: 'DAU',
    categoryNameCN: '日活跃玩家数',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_dau_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_ampere_dau_category_disabled_tips',
  }),
  ...appendFields2ListData(ampereEngagementList, {
    category: 'engagement',
    categoryNameEN: 'Engagement',
    // categoryNameCN: '参与度',
    categoryNameCN: '活跃度',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_engagement_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_ampere_engagement_category_disabled_tips',
  }),
  ...appendFields2ListData(ampereRetentionList, {
    category: 'retention',
    categoryNameEN: 'Retention',
    categoryNameCN: '留存',
    categoryDisabledTipsLocaleKey: 'dbi_info_ampere_retention_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_ampere_retention_category_disabled_tips',
  }),
  // ...appendFields2ListData(newzooChurnAcquisitionList, {
  //   category: 'churnAcquisition',
  //   categoryNameEN: 'Churn/Acquisition',
  //   categoryNameCN: '流失/获客',
  //   categoryDisabledTipsLocaleKey: 'dbi_info_newzoo_metrics_category_disabled_tips',
  //   metricDisabledTipsLocaleKey: 'dbi_info_newzoo_metrics_category_disabled_tips',
  // }),
  // ...appendFields2ListData(newzooTimeSpentList, {
  //   category: 'timeSpent',
  //   categoryNameEN: 'Time Spent',
  //   categoryNameCN: '游玩时长',
  //   categoryDisabledTipsLocaleKey: 'dbi_info_newzoo_metrics_category_disabled_tips',
  //   metricDisabledTipsLocaleKey: 'dbi_info_newzoo_metrics_category_disabled_tips',
  // }),
  // ...appendFields2ListData(newzooRetentionList, {
  //   category: 'retention',
  //   categoryNameEN: 'Retention',
  //   categoryNameCN: '留存',
  //   categoryDisabledTipsLocaleKey: 'dbi_info_newzoo_metrics_category_disabled_tips',
  //   metricDisabledTipsLocaleKey: 'dbi_info_newzoo_metrics_category_disabled_tips',
  // }),
  // ...appendFields2ListData(storeRatingList, {
  //   category: 'storeRating',
  //   categoryNameEN: 'Store Rating',
  //   categoryNameCN: '商店评分',
  //   categoryDisabledTipsLocaleKey: 'dbi_info_store_rating_disabled_tips',
  //   metricDisabledTipsLocaleKey: 'dbi_info_store_rating_disabled_tips',
  // }),
  ...appendFields2ListData(newzooList, {
    category: 'noUpdate',
    categoryNameEN: 'No Update',
    categoryNameCN: '无更新',
    categoryDisabledTipsLocaleKey: 'dbi_info_newzoo_noupdate_metrics_category_disabled_tips',
    metricDisabledTipsLocaleKey: 'dbi_info_newzoo_noupdate_metrics_category_disabled_tips',
  }),
];

const orderPCConsoleCategoryOptions = (options: OptionsItemType[]): OptionsItemType[] => {
  const orderMap = new Map(
    PC_CONSOLE_DISPLAY_CATEGORY_ORDER.map((category, index) => [category, index]),
  );
  return [...options].sort(
    (left, right) =>
      (orderMap.get(left?.value as string) ?? Number.MAX_SAFE_INTEGER) -
      (orderMap.get(right?.value as string) ?? Number.MAX_SAFE_INTEGER),
  );
};

const orderPCConsoleMetricChildren = (
  options: OptionsItem[],
  metricsMap: Record<string, any>,
): OptionsItem[] =>
  options.map((option) => ({
    ...option,
    children: [...(option?.children || [])].sort((left, right) => {
      const leftSource = metricsMap?.[left?.value]?.dataSource;
      const rightSource = metricsMap?.[right?.value]?.dataSource;
      const leftOrder = getPCConsoleSourceOrder(leftSource);
      const rightOrder = getPCConsoleSourceOrder(rightSource);
      return leftOrder - rightOrder;
    }),
  }));

const {
  options: pcConsoleCascaderMetricsOptionsRaw,
  map: pcConsoleCascaderMetricsMap,
} = aggregateCategoryMetrics(pcConsoleCategoryMetrics);
const pcConsoleCascaderMetricsOptions = orderPCConsoleCategoryOptions(
  pcConsoleCascaderMetricsOptionsRaw,
);
const pcConsoleCascaderMetricsOptionsOrdered = orderPCConsoleMetricChildren(
  pcConsoleCascaderMetricsOptions,
  pcConsoleCascaderMetricsMap,
);
const { options: pcConsoleDatabrainOnlyCascaderMetricsOptions, map: pcConsoleDatabrainOnlyCascaderMetricsMap } =
  aggregateCategoryMetrics(pcConsoleDatabrainCategoryMetrics);

const pcConsoleCascaderMetricsOptionsMerged = pcConsoleCascaderMetricsOptionsOrdered;

export const PCConsoleSegementList = {
  pcConsoleCascaderMetricsOptions: pcConsoleCascaderMetricsOptionsMerged,
  pcConsoleCascaderMetricsMap: {
    ...pcConsoleCascaderMetricsMap,
    ...pcConsoleDatabrainOnlyCascaderMetricsMap,
  },
  pcConsoleDefaultMetrics: PC_CONSOLE_DEFAULT_METRICS,
};

const getMergedPCConsoleDatabrainOptions = () => {
  const databrainRevenueOption = pcConsoleDatabrainOnlyCascaderMetricsOptions.find(
    // Databrain 专用源列表仍使用旧分类 revenue，作为稳定的内部配置值。
    (item) => item?.value === 'revenue',
  );
  if (!databrainRevenueOption || !Array.isArray(databrainRevenueOption.children)) {
    return pcConsoleCascaderMetricsOptionsMerged;
  }
  const databrainChildren = databrainRevenueOption.children;
  if (!databrainChildren.length) {
    return pcConsoleCascaderMetricsOptionsMerged;
  }
  const salesCategory = getPCConsoleDisplayCategory('revenue');
  return pcConsoleCascaderMetricsOptionsMerged.map((option) => {
    if (option?.value !== salesCategory) {
      return option;
    }
    const existingChildren = option?.children || [];
    const uniqueDatabrainChildren = databrainChildren.filter(
      (child) => !existingChildren.some((existing) => existing?.value === child?.value),
    );
    if (!uniqueDatabrainChildren.length) {
      return option;
    }
    return {
      ...option,
      children: [...uniqueDatabrainChildren, ...existingChildren],
    };
  });
};

export const PCConsoleDataBrainSegementList = {
  pcConsoleCascaderMetricsOptions: getMergedPCConsoleDatabrainOptions(),
  pcConsoleCascaderMetricsMap: {
    ...pcConsoleCascaderMetricsMap,
    ...pcConsoleDatabrainOnlyCascaderMetricsMap,
  },
  pcConsoleDefaultMetrics: PC_CONSOLE_DATABRAIN_DEFAULT_METRICS,
};

export const PCConsoleMetricSegementList = [...steamSalesDetailList, ...newzooList];

// AllMetrics 下拉分组按钮是按 dataSourceType 分组的：
// - Steam 分组（dataSourceType=steamcharts）原本塞进了 alinea/gamalytic/vgi 的 PCU/ACU/Followers/Reviews，
//   只保留 source_steam pcu/acu 和 Steam 关注人数（steamfollowers）；
// - 其它指标回到自己原生的数据源分组，因此需要在 AllMetrics 层面把 dataSourceType 覆盖回 alinea/gamalytic/vginsights。
// 底层常量被其它页面共用，不在源头改动，只在此处按需覆盖。
const _overrideDataSourceType = <T extends { dataSourceType?: string }>(
  list: T[],
  target: string,
): T[] => list.map((item) => ({ ...item, dataSourceType: target }));

const allMetrics_SteamGroup = [...sourceSteamPCUList, ...steamFollowers];
const allMetrics_AlineaGroup = [
  ..._overrideDataSourceType(alineaMetricList, dataSourceType.alinea),
];
const allMetrics_GamalyticGroup = [
  ..._overrideDataSourceType(gamalyticMetricList, dataSourceType.gamalytic),
];
const allMetrics_VGIGroup = [
  ..._overrideDataSourceType(pcMetricTableSteamSaleList, dataSourceType.vginsights),
];

const allMetricsOptions_tmp = [
  ...digitalRevenueList,
  ...npdList,
  ...gsdSegmentList,
  ...allMetrics_SteamGroup,
  ...allMetrics_AlineaGroup,
  ...allMetrics_GamalyticGroup,
  ...allMetrics_VGIGroup,
  // ...japanSalesList,
  ...newzooMAUList,
  ...newzooEngagementList,
  ...newzooChurnAcquisitionList,
  ...newzooTimeSpentList,
  ...newzooRetentionList,
];

export const allMetricsOptions = allMetricsOptions_tmp.map((t) => {
  const displayCategory = getPCConsoleMetricDisplayCategory(t);
  const value = t?.dataSource === 'famitsu' ? `${t?.dataSource}_${t?.value}` : t?.value;
  return {
    ...t,
    displayCategory,
    ...PC_CONSOLE_DISPLAY_CATEGORY_FIELDS[displayCategory],
    ...resolveAllMetricsMetricBoxMeta({ ...t, value }),
    // allmetrics的'est_price'没有展示拆线
    // width: t?.value === 'est_price' || t?.value === 'gamalytic__est_price' ? 220 : t?.width,
    // width_cn: t?.value === 'est_price' || t?.value === 'gamalytic__est_price' ? 190 : t?.width_cn,
    value,
    infoDetail: t?.info, // allmetrics详情用榜单的提示
    infoSuffix:
      t?.dataSource === 'vginsights'
        ? 'dbi_info_vig_disabled'
        : t?.dataSource === 'gamalytic'
        ? 'dbi_info_vig_disabled'
        : t?.dataSource === 'famitsu'
        ? 'dbi_info_famitsu_disabled'
        : t?.dataSource === GSD_SOURCE_KEY
        ? 'dbi_info_gsd_disabled'
        : '',
  };
});
export const allMetricsTypes = [
  { text: 'dbi_active_users', value: segType.activeuser },
  { text: 'dbi_revenue', value: segType.revenue },
  // { text: 'dbi_metric_engagement', value: segType.engagement },
  { text: 'dbi_metric_engagement_active_level', value: segType.engagement },
  { text: 'dbi_metric_churn_acquisition', value: segType.churnacq },
  { text: 'dbi_metric_atime_spent_0H', value: segType.timeSpent },
  { text: 'dbi_metric_retention', value: segType.retention },
];
export const allMetricsSourcesOption = [
  { text: 'DataBrain', value: 'databrain', icon: 'databrainbluebg' },
  { text: 'M Science', value: 'mscience', icon: 'mscience' },
  { text: 'GSD', value: GSD_SOURCE_KEY, icon: 'gsd' },
  { text: 'Steam', value: 'steamcharts', icon: 'source_steam' },
  { text: 'Alinea Analytics', value: 'alinea', icon: 'alinea' },
  { text: 'dbi_other_vendor', value: 'ampere', icon: 'ampere' },
  { text: 'Gamalytic', value: 'gamalytic', icon: 'gamalytic' },
  { text: 'VGI', value: 'vginsights', icon: 'vginsights' },
  { text: 'NPD', value: 'npd', icon: 'npd' },
  // { text: 'Famitsu', value: 'famitsu', icon: 'famitsu' },
  { text: 'dbi_pc_console_metric_type_no_update', value: 'newzoo', icon: 'newzoo' },
].sort((left, right) => getPCConsoleSourceOrder(left.value) - getPCConsoleSourceOrder(right.value));
